<template>
  <section class="tp-center">
    <div v-if="!leader" class="empty">请从左侧选择龙头</div>
    <template v-else>
      <header v-if="!hideStockHead" class="stock-head">
        <div class="name-line">
          <span class="name">{{ leader.name }}</span>
          <span class="code">{{ leader.code }}</span>
          <span class="chg" :class="chgClass(leader.changePct)">{{ formatPct(leader.changePct) }}</span>
          <span v-if="priceDelta != null" class="delta" :class="chgClass(priceDelta)">{{ formatDelta(priceDelta) }}</span>
          <span v-if="leader.closePrice != null" class="px">
            <span class="lbl">收盘价</span>
            <b>{{ leader.closePrice }}</b>
          </span>
          <span class="since">
            <span class="lbl">龙头以来涨幅</span>
            <b :class="chgClass(leaderSinceDisplay)">{{ formatPct(leaderSinceDisplay) }}</b>
          </span>
          <button
            type="button"
            class="concept-btn"
            :disabled="pack.loadingConcepts || !(pack.concepts && pack.concepts.length)"
            @click="conceptDialogVisible = true"
          >概念详情</button>
        </div>
        <div v-if="pack.loadingConcepts" class="chips-hint">正在读取概念（与补涨分析同源）…</div>
        <div v-else-if="!(pack.concepts && pack.concepts.length)" class="chips-hint">当日涨停池与个股资料均无概念</div>
        <div v-else-if="selectedTheme" class="selected-theme-hint">
          当前题材 <b>{{ selectedTheme }}</b>
        </div>
      </header>

      <el-dialog
        title="概念详情"
        :visible.sync="conceptDialogVisible"
        width="720px"
        append-to-body
        custom-class="tp-concept-dialog"
      >
        <div class="chips dialog-chips">
          <button
            v-for="c in pack.concepts"
            :key="c"
            type="button"
            class="chip"
            :class="{ on: c === selectedTheme }"
            @click="pickConcept(c)"
          >{{ c }}</button>
        </div>
      </el-dialog>

      <div class="diffusion-block">
        <div class="diffusion-head">
          <span class="diffusion-ico">▣</span>
          <div class="diffusion-titles">
            <span class="diffusion-title">题材高度扩散</span>
            <span class="diffusion-sub">观察龙头以来市场可接受的补涨高度（本轮仅非龙头最高高度；点添加后到补涨池点选入池）</span>
          </div>
          <el-button
            size="mini"
            type="primary"
            class="sync-ths-btn"
            :loading="syncThsLoading"
            :title="thsSyncTitle"
            @click="syncThemePropToThs"
          >同步同花顺</el-button>
        </div>
        <div v-if="!hideStockHead" class="diffusion-metrics">
          <div class="metric">
            <div class="metric-label">非龙头最高高度</div>
            <div class="metric-value">{{ nonLeaderMaxHeightLabel }}</div>
          </div>
          <div
            v-for="(slot, i) in mainAttrs"
            :key="'main-attr-' + i"
            class="attr-slot"
            :class="{ filled: !!slot, next: mainAttrPickMode && nextMainAttrIndex === i }"
          >
            <span class="attr-slot-label">主属性{{ i + 1 }}</span>
            <span v-if="slot" class="attr-slot-name" :title="attrLabel(slot)">{{ attrLabel(slot) }}</span>
            <button
              type="button"
              class="manual-add attr-add"
              :class="{ on: mainAttrPickMode && nextMainAttrIndex === i }"
              @click="startMainAttrPick"
            >{{ mainAttrPickMode && nextMainAttrIndex === i ? '点选中' : '添加' }}</button>
            <button v-if="slot" type="button" class="attr-clear" title="取消" @click="clearMainAttr(i)">×</button>
          </div>
        </div>
        <div v-if="mainAttrPickMode" class="pick-hint">
          点选下方「龙头题材传播」题材，按顺序填入主属性（再点已选题材可取消）
          <button type="button" class="manual-add on" @click="mainAttrPickMode = false">完成点选</button>
        </div>
        <div class="height-boards">
          <div class="manual-pool">
            <div class="manual-pool-h pool-switch height-board-h">
              <button type="button" class="pool-tab" :class="{ on: heightBoardTab === 'height' }" @click="heightBoardTab = 'height'">非龙头最高高度</button>
              <button type="button" class="pool-tab" :class="{ on: heightBoardTab === 'd10' }" @click="heightBoardTab = 'd10'">题材10日涨幅</button>
              <button type="button" class="pool-tab" :class="{ on: heightBoardTab === 'd15' }" @click="heightBoardTab = 'd15'">题材15日涨幅</button>
              <button type="button" class="pool-tab" :class="{ on: heightBoardTab === 'd20' }" @click="heightBoardTab = 'd20'">题材20日涨幅</button>
              <button
                v-if="heightBoardTab === 'height'"
                type="button"
                class="manual-add"
                :class="{ on: heightPickMode }"
                @click="toggleHeightPickMode"
              >{{ heightPickMode ? '完成点选' : '添加' }}</button>
            </div>
            <div v-if="heightBoardTab === 'height' && heightPickMode" class="pick-hint">点选模式已开：可点击上方趋势龙、连板龙卡片，或下方「老臣记录池」中的股票入池（再次点击可取消）</div>
            <div v-if="heightBoardTab === 'height' && leaderPickMode" class="pick-hint">点选本表股票，作为首板预选的龙头（再点已选龙头可恢复当前龙头卡）</div>
            <div v-else-if="heightBoardTab === 'height' && buZhangPickMode" class="pick-hint">点选本表股票，按顺序填入首板预选的补涨1、补涨2、补涨3（再点可取消）</div>
            <div v-if="heightBoardTab !== 'height'" class="manual-table-wrap">
              <table class="manual-table" :class="{ 'role-picking': heightBoardTab === 'd10' && !!rolePick }">
                <thead>
                  <tr>
                    <th class="th-name">名称</th>
                    <th class="th-pct">总涨幅</th>
                    <th class="th-ff">自由流通</th>
                    <th class="th-concept">概念</th>
                    <th class="th-mood">状态</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="row in activeThemeGainRows"
                    :key="row.code"
                    :class="{ pickable: heightBoardTab === 'd10' && !!rolePick }"
                    @click="onThemeGainRowClick(row)"
                  >
                    <td class="nm">{{ row.name || '—' }}</td>
                    <td class="pct" :class="chgClass(row.totalReturnPct)">{{ formatPct(row.totalReturnPct) }}</td>
                    <td class="ff">{{ formatFreeFloatYi(row.freeFloatMarketCapYi) }}</td>
                    <td class="concept">
                      <template v-if="row.concepts && row.concepts.length">
                        <span v-for="name in row.concepts" :key="name" class="concept-tag hot">{{ name }}</span>
                      </template>
                      <span v-else class="concept-empty">—</span>
                    </td>
                    <td class="mood">
                      <span v-for="tag in row.mood" :key="tag" class="mood-tag">{{ tag }}</span>
                      <span v-if="!row.mood || !row.mood.length">—</span>
                    </td>
                  </tr>
                  <tr v-if="themeGainLoading">
                    <td colspan="5" class="manual-empty">统计中…</td>
                  </tr>
                  <tr v-else-if="!activeThemeGainRows.length">
                    <td colspan="5" class="manual-empty">{{ themeGainEmptyText }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div v-else class="manual-table-wrap">
              <table class="manual-table" :class="{ 'bz-picking': buZhangPickMode || leaderPickMode, 'role-picking': !!rolePick }">
                <thead>
                  <tr>
                    <th class="th-name">名称</th>
                    <th class="th-h">连板高度</th>
                    <th class="th-ndm">最终板</th>
                    <th class="th-pct">总涨幅</th>
                    <th class="th-ff">自由流通</th>
                    <th class="th-prem">1-2溢价</th>
                    <th class="th-concept">概念</th>
                    <th class="th-mood">状态</th>
                    <th class="col-act" />
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="(row, idx) in heightManualRows"
                    :key="row.id"
                    :class="{ 'bz-picked': buZhangIndex(row) >= 0 || isFbLeaderRow(row), pickable: !!rolePick }"
                    @click="onHeightManualRowClick(row)"
                  >
                    <td class="nm">
                      {{ row.name || '—' }}
                      <span v-if="isFbLeaderRow(row)" class="attr-mark">龙头</span>
                      <span v-if="buZhangIndex(row) >= 0" class="attr-mark">补{{ buZhangIndex(row) + 1 }}</span>
                    </td>
                    <td class="h">{{ row.lianBanHeight || '—' }}</td>
                    <td class="ndm">{{ row.finalBoardLabel || '—' }}</td>
                    <td class="pct" :class="chgClass(parseReturnPct(row.totalReturnText))">{{ formatHeightReturn(row.totalReturnText) }}</td>
                    <td class="ff">{{ formatFreeFloatYi(row.freeFloatMarketCapYi) }}</td>
                    <td class="prem" :class="chgClass(row.premium12Pct)">{{ formatPct(row.premium12Pct) }}</td>
                    <td class="concept">
                      <template v-if="conceptTagsForHeightRow(row).length">
                        <span
                          v-for="tag in conceptTagsForHeightRow(row)"
                          :key="tag.name"
                          class="concept-tag"
                          :class="{ hot: tag.hot }"
                        >{{ tag.name }}</span>
                      </template>
                      <span v-else class="concept-empty">—</span>
                    </td>
                    <td class="mood">
                      <span v-for="tag in heightMoodTags(row)" :key="tag" class="mood-tag">{{ tag }}</span>
                      <span v-if="!heightMoodTags(row).length">—</span>
                    </td>
                    <td class="col-act">
                      <button type="button" class="manual-del" title="移除" @click.stop="removeHeightRow(idx)">×</button>
                    </td>
                  </tr>
                  <tr v-if="!heightManualRows.length">
                    <td colspan="9" class="manual-empty">暂无名单，点击「添加」后在下方补涨池点选入池</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
          <div class="relay-pool">
            <div class="manual-pool-h pool-switch">
              <button type="button" class="pool-tab" :class="{ on: upperPool === 'relay' }" @click="upperPool = 'relay'">今日补涨接力TOP池</button>
              <button type="button" class="pool-tab" :class="{ on: upperPool === 'fbpre' }" @click="upperPool = 'fbpre'">今日首板预选（主板）</button>
              <button type="button" class="pool-tab" :class="{ on: upperPool === 'fbchi' }" @click="upperPool = 'fbchi'">今日首板预选（创业）</button>
            </div>
            <div v-if="upperPool === 'relay' && relayPickMode" class="pick-hint">点选已开：在下方「补涨分析」勾选股票加入本池（再次点击取消）</div>
            <div v-if="upperPool === 'fbpre' && fbPreselectPickMode" class="pick-hint">点选已开：在下方「首板预选（主板）」勾选股票加入本池（再次点击取消）</div>
            <div v-if="upperPool === 'fbchi' && fbChiPickMode" class="pick-hint">点选已开：在下方「首板预选（创业）」勾选股票加入本池（再次点击取消）</div>
            <div v-if="upperPool === 'fbpre' || upperPool === 'fbchi'" class="manual-table-wrap relay-table-wrap">
              <table class="manual-table" :class="{ 'role-picking': !!rolePick }">
                <thead>
                  <tr>
                    <th class="th-name">名称</th>
                    <th class="th-chip">收盘价</th>
                    <th class="th-chip">中期筹码</th>
                    <th class="th-ff">流通市值</th>
                    <th class="th-score">概念分</th>
                    <th class="th-concept">概念</th>
                    <th class="col-act" />
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(row, idx) in activeFbPreselectRows" :key="row.id">
                    <td class="nm">{{ row.name || '—' }}</td>
                    <td class="chip">{{ formatFbPrice(row.currentPrice) }}</td>
                    <td class="chip">{{ formatFbChip(row.midChip) }}</td>
                    <td class="ff">{{ formatRelayMarketYi(row.marketValue) }}</td>
                    <td class="score">{{ formatRelayPreScreen(row.matchScore) }}</td>
                    <td class="concept">
                      <template v-if="row.concepts && row.concepts.length">
                        <span
                          v-for="(tag, ti) in row.concepts"
                          :key="tag.name + ti"
                          class="concept-tag"
                          :class="tag.kind === 'required' ? 'hot' : ''"
                        >{{ tag.name }}{{ tag.kind === 'selected' ? '(+5)' : '' }}</span>
                      </template>
                      <span v-else class="concept-empty">—</span>
                    </td>
                    <td class="col-act">
                      <button type="button" class="manual-del" title="移除" @click.stop="removeActiveFbPreselectRow(idx)">×</button>
                    </td>
                  </tr>
                  <tr v-if="!activeFbPreselectRows.length">
                    <td colspan="7" class="manual-empty">{{ upperPool === 'fbchi' ? '暂无，点击「添加」后在首板预选（创业）中勾选股票' : '暂无，点击「添加」后在首板预选（主板）中勾选股票' }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div v-else class="manual-table-wrap relay-table-wrap">
              <table class="manual-table" :class="{ 'role-picking': !!rolePick }">
                <thead>
                  <tr>
                    <th class="th-name">名称</th>
                    <th class="th-chip">中期筹码分</th>
                    <th class="th-score">综合分</th>
                    <th class="th-chip">全周期粘合</th>
                    <th class="th-ff">自由流通</th>
                    <th class="th-h">梯队</th>
                    <th class="th-score">概念分和</th>
                    <th class="th-concept">匹配概念</th>
                    <th class="col-act" />
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="(row, idx) in relayTopRows"
                    :key="row.id"
                    :class="{ pickable: !!rolePick }"
                    @click="onRelayRowClick(row)"
                  >
                    <td class="nm">{{ row.name || '—' }}</td>
                    <td class="chip">{{ formatRelayMidChip(row.midChip) }}</td>
                    <td class="score">{{ formatRelayPreScreen(row.preScreenScore) }}</td>
                    <td class="chip">{{ formatRelayRangeAll(row.rangeAllD0) }}</td>
                    <td class="ff">{{ formatRelayMarketYi(row.marketValue) }}</td>
                    <td class="h">
                      <span
                        v-if="row.nodeHit"
                        class="node-star"
                        :style="relayNodeStarStyle(row.nodeScore)"
                        :title="relayNodeStarTitle(row)"
                      >★</span><span :style="row.nodeHit ? relayNodeStarStyle(row.nodeScore) : null">{{ row.boardLabel || '—' }}</span>
                    </td>
                    <td class="score">{{ formatRelayConceptSum(row.conceptScoreSum) }}</td>
                    <td class="concept">
                      <template v-if="row.matchedConcepts && row.matchedConcepts.length">
                        <span
                          v-for="(tag, ti) in row.matchedConcepts"
                          :key="tag.name + ti"
                          class="concept-tag"
                          :class="relayConceptTagClass(tag)"
                        >{{ tag.name }}</span>
                      </template>
                      <span v-else class="concept-empty">—</span>
                    </td>
                    <td class="col-act">
                      <button type="button" class="manual-del" title="移除" @click.stop="removeRelayRow(idx)">×</button>
                    </td>
                  </tr>
                  <tr v-if="!relayTopRows.length">
                    <td colspan="9" class="manual-empty">暂无，点击「添加」后在补涨分析中勾选股票</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </template>

    <slot />

    <template v-if="leader">
      <div class="catchup-block" :class="{ 'pick-active': heightPickMode }">
        <div class="catchup-head">
          <span class="catchup-ico">⌂</span>
          <span class="catchup-title">老臣记录池</span>
          <span v-if="heightPickMode" class="pick-badge">点选入池中</span>
          <div class="veteran-tabs">
            <button type="button" :class="{ on: veteranPane === 'preselect' }" @click="veteranPane = 'preselect'">预选池</button>
            <button type="button" :class="{ on: veteranPane === 'confirm' }" @click="veteranPane = 'confirm'">确认池</button>
          </div>
          <button v-if="veteranPane === 'preselect'" type="button" class="veteran-save" @click="saveVeteranChecked">保存</button>
          <button type="button" class="veteran-clear" @click="clearVeteranPool">清空</button>
        </div>
        <div class="pools">
          <div v-for="col in poolCols" :key="col.key" class="pool" :class="'tone-' + col.tone">
            <div class="pool-top">
              <span class="pool-tag">{{ col.title }}</span>
              <div class="pool-stats">
                <span>数量 <b>{{ col.count }}</b></span>
                <span>总涨幅 <b class="up">{{ formatPct(col.totalReturnPct) }}</b></span>
                <span>今日红盘率 <b class="up">{{ formatPlainPct(col.todayRedRatePct) }}</b></span>
              </div>
            </div>
            <div class="pool-table-wrap">
              <table class="pool-table" :class="{ picking: heightPickMode || !!rolePick }">
                <colgroup>
                  <col v-if="veteranPane === 'preselect'" class="col-check" />
                  <col class="col-idx" />
                  <col class="col-name" />
                  <col class="col-code" />
                  <col class="col-board" />
                  <col class="col-pct" />
                  <col class="col-pct" />
                  <col class="col-pct" />
                </colgroup>
                <thead>
                  <tr>
                    <th v-if="veteranPane === 'preselect'" />
                    <th>#</th>
                    <th>股票名称</th>
                    <th>代码</th>
                    <th>最终板数</th>
                    <th>总涨幅</th>
                    <th>今日涨幅</th>
                    <th>1-2溢价</th>
                  </tr>
                </thead>
                <tbody>
                  <template v-for="item in col.displayRows">
                    <tr v-if="item.kind === 'head'" :key="'h-' + col.key + '-' + item.title" class="grp-head">
                      <td :colspan="veteranPane === 'preselect' ? 8 : 7">{{ item.title }}</td>
                    </tr>
                    <tr
                      v-else
                      :key="col.key + '-' + item.row.code"
                      :class="{
                        pickable: heightPickMode || !!rolePick,
                        picked: isHeightPicked(item.row.code)
                      }"
                      @click="onPoolRowClick(item.row)"
                    >
                      <td v-if="veteranPane === 'preselect'" class="chk" @click.stop>
                        <input
                          type="checkbox"
                          :checked="isVeteranChecked(item.row.code)"
                          @change="toggleVeteranCheck(item.row.code, $event)"
                        >
                      </td>
                      <td>{{ item.idx }}</td>
                      <td class="nm" :title="item.row.name">{{ item.row.name }}</td>
                      <td class="code">{{ item.row.code }}</td>
                      <td>{{ item.row.board }}</td>
                      <td :class="chgClass(item.row.totalReturnPct)">{{ formatPct(item.row.totalReturnPct) }}</td>
                      <td :class="chgClass(item.row.todayChgPct)">{{ formatPct(item.row.todayChgPct) }}</td>
                      <td :class="chgClass(item.row.premium12Pct)">{{ formatPct(item.row.premium12Pct) }}</td>
                    </tr>
                  </template>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      <div class="catchup-block concept-analysis">
        <div class="catchup-head">
          <span class="catchup-ico">▣</span>
          <span class="catchup-title">补涨概念分析</span>
        </div>
        <div v-if="!fbConcepts.length" class="concept-analysis-empty">暂无出现不少于 2 次的概念</div>
        <div v-else class="fb-concepts">
          <el-badge
            v-for="c in fbConcepts"
            :key="c.name"
            :value="c.count"
            type="primary"
            class="fb-count-badge"
          >
            <el-tag
              size="mini"
              class="fb-concept-tag"
              :type="fbSubAttrHit(c.name) ? 'danger' : ''"
              @click.native.stop="openFbAttrMenu($event, c.name)"
            >{{ c.name }}</el-tag>
          </el-badge>
        </div>
        <div
          v-if="fbAttrMenu.visible"
          class="fb-attr-menu"
          :style="{ left: fbAttrMenu.x + 'px', top: fbAttrMenu.y + 'px' }"
          @click.stop
        >
          <div class="fb-attr-menu__title">填入「{{ fbAttrMenu.concept }}」</div>
          <button
            v-for="slot in fbSubSlotDefs"
            :key="slot.key"
            type="button"
            class="fb-attr-menu__item"
            @click="assignFbSubAttr(slot.key)"
          >
            <span>{{ slot.label }}</span>
            <span v-if="fbSubAttrs[slot.key]" class="fb-attr-menu__cur">{{ fbSubAttrs[slot.key] }}</span>
          </button>
        </div>
      </div>

      <div class="block" :class="{ 'pick-active': relayPickMode || fbPreselectPickMode || fbChiPickMode || mainAttrPickMode }">
        <div class="block-h">
          <div class="block-tabs">
            <button
              type="button"
              class="block-tab"
              :class="{ on: bottomTab === 'theme' }"
              @click="setBottomTab('theme')"
            >龙头题材传播<span v-if="mainAttrPickMode" class="tab-badge">点选主属性</span></button>
            <button
              type="button"
              class="block-tab"
              :class="{ on: bottomTab === 'catchup' }"
              @click="setBottomTab('catchup')"
            >补涨分析</button>
            <button
              type="button"
              class="block-tab"
              :class="{ on: bottomTab === 'firstboard' }"
              @click="setBottomTab('firstboard')"
            >首板预选（主板）<span v-if="leaderPickMode" class="tab-badge">点选龙头</span><span v-else-if="buZhangPickMode" class="tab-badge">点选补涨</span></button>
            <button
              type="button"
              class="block-tab"
              :class="{ on: bottomTab === 'firstboardChi' }"
              @click="setBottomTab('firstboardChi')"
            >首板预选（创业）<span v-if="leaderPickMode" class="tab-badge">点选龙头</span><span v-else-if="buZhangPickMode" class="tab-badge">点选补涨</span></button>
            <span v-if="relayPickMode || fbPreselectPickMode || fbChiPickMode" class="pick-badge">勾选入池</span>
          </div>
        </div>
        <div v-show="bottomTab === 'theme'" class="table-wrap" :class="{ 'attr-picking': mainAttrPickMode }">
          <table class="theme-table">
            <thead>
              <tr>
                <th>#</th>
                <th>题材名称</th>
                <th class="th-stack th-sort" :class="{ on: sortKey === 'sinceLeaderPct' }" @click="setSort('sinceLeaderPct')">
                  <span>龙头首板</span>
                  <span>以来</span>
                  <span>题材涨幅{{ sortMark('sinceLeaderPct') }}</span>
                </th>
                <th class="th-stack th-sort" :class="{ on: sortKey === 'recent5dPct' }" @click="setSort('recent5dPct')">
                  <span>近5日</span>
                  <span>题材涨幅{{ sortMark('recent5dPct') }}</span>
                </th>
                <th class="th-stack th-sort" :class="{ on: sortKey === 'recent10dPct' }" @click="setSort('recent10dPct')">
                  <span>近10日</span>
                  <span>题材涨幅{{ sortMark('recent10dPct') }}</span>
                </th>
                <th class="th-sort" :class="{ on: sortKey === 'cumLimitUpN' }" @click="setSort('cumLimitUpN')">
                  累计涨停数{{ sortMark('cumLimitUpN') }}
                </th>
                <th class="th-stack th-sort" :class="{ on: sortKey === 'recent10MainNetYi' }" @click="setSort('recent10MainNetYi')">
                  <span>近10日</span>
                  <span>主力净额</span>
                  <span>（亿）{{ sortMark('recent10MainNetYi') }}</span>
                </th>
                <th>独立涨停数</th>
                <th>平均红盘率</th>
                <th>今日红盘率</th>
                <th>今日涨停数</th>
                <th>成交额 MA10</th>
                <th>曾≥4B</th>
                <th>曾 3B</th>
                <th>曾 2B</th>
                <th>题材热度</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(row, idx) in visibleRows"
                :key="row.name"
                :class="{ on: row.name === selectedTheme, 'attr-picked': mainAttrIndex(row.name) >= 0 }"
                @click="onThemeRowClick(row)"
              >
                <td>{{ idx + 1 }}</td>
                <td class="name-cell">{{ row.name }}<span v-if="mainAttrIndex(row.name) >= 0" class="attr-mark">主{{ mainAttrIndex(row.name) + 1 }}</span></td>
                <td :class="chgClass(row.sinceLeaderPct)">{{ formatPct(row.sinceLeaderPct) }}</td>
                <td :class="chgClass(row.recent5dPct)">{{ formatPct(row.recent5dPct) }}</td>
                <td :class="chgClass(row.recent10dPct)">{{ formatPct(row.recent10dPct) }}</td>
                <td>{{ dash(row.cumLimitUpN) }}</td>
                <td :class="chgClass(row.recent10MainNetYi)">{{ formatYi(row.recent10MainNetYi) }}</td>
                <td>{{ dash(row.indepLimitUpN) }}</td>
                <td>{{ formatPlainPct(row.avgRedRatePct) }}</td>
                <td class="red">{{ formatPlainPct(row.todayRedRatePct) }}</td>
                <td class="red">{{ dash(row.todayLimitUpN) }}</td>
                <td>{{ formatMult(row.amountMa10) }}</td>
                <td>{{ dash(row.board4PlusN) }}</td>
                <td>{{ dash(row.board3N) }}</td>
                <td>{{ dash(row.board2N) }}</td>
                <td>
                  <span class="heat">
                    <span class="bars">
                      <i v-for="n in 5" :key="n" :class="{ on: n <= (row.heatLevel || 0) }" />
                    </span>
                    {{ row.heatLabel || '—' }}
                  </span>
                </td>
              </tr>
              <tr v-if="!rankedRows.length">
                <td colspan="16" class="empty-row">{{ pack.loadingConcepts || pack.loadingReturns ? '正在读取题材涨幅…' : '没有可排名的题材涨幅' }}</td>
              </tr>
              <tr v-else-if="hiddenThemeCount > 0" class="expand-row">
                <td colspan="16">
                  <button type="button" class="expand-btn" @click="themeExpanded = !themeExpanded">
                    {{ themeExpanded ? '收起题材列表' : `展开其余 ${hiddenThemeCount} 个题材` }}
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div v-if="catchupPaneReady" v-show="bottomTab === 'catchup'" class="catchup-embed" :class="{ 'pick-active': relayPickMode }">
          <div v-if="relayPickMode" class="pick-hint">勾选表格左侧方框，把补涨分析中的股票加入「今日补涨接力TOP池」</div>
          <GapUpAnalysis
            ref="catchupGap"
            embed-results
            :trade-date="tradeDate"
            :embed-leader="leader"
            :embed-pick-active="relayPickMode"
            :embed-picked-codes="relayPickedCodes"
            :embed-highlight-concepts="mainAttrBareNames"
            :embed-dragon-cards="dragonCards"
            :embed-dragon-selected="selectedDragonKeys"
            @embed-dragon-toggle="toggleDragonCard"
            @embed-pick="toggleRelayFromAnalysis"
            @auto-relay="autoFillRelayFromAnalysis"
            @relay-add="toggleRelayPickMode"
          />
        </div>
        <div v-show="bottomTab === 'firstboard' || bottomTab === 'firstboardChi'" class="firstboard-embed" :class="{ 'pick-active': buZhangPickMode || leaderPickMode || fbPreselectPickMode || fbChiPickMode }">
          <div v-if="bottomTab === 'firstboard' && fbPreselectPickMode" class="pick-hint">勾选表格中的股票，加入「今日首板预选（主板）」</div>
          <div v-if="bottomTab === 'firstboardChi' && fbChiPickMode" class="pick-hint">勾选表格中的创业板股票，加入「今日首板预选（创业）」</div>
          <div class="buzhang-row">
            <div class="dragon-card-row">
              <span class="search-area-label">龙头</span>
              <button
                v-for="card in dragonCards"
                :key="'fb-' + card.key"
                type="button"
                class="dragon-card"
                :class="['tone-' + card.role, { on: selectedDragonKeys.indexOf(card.key) >= 0 }]"
                @click="toggleDragonCard(card)"
              >
                <em>{{ card.tag }}</em>
                <b>{{ card.name }}</b>
              </button>
            </div>
            <span class="leader-quote" :title="fbLeaderMeta.title">
              <span class="lbl">价格</span>
              <b>{{ fbLeaderMeta.priceText || '—' }}</b>
              <span class="lbl">自由流通市值</span>
              <b>{{ fbLeaderMeta.ffText || '—' }}</b>
            </span>
            <div
              v-for="(slot, i) in buZhangSlots"
              :key="'bz-' + i"
              class="attr-slot"
              :class="{ filled: !!(slot && slot.code), next: buZhangPickMode && nextBuZhangIndex === i }"
            >
              <span class="attr-slot-label">补涨{{ i + 1 }}</span>
              <span v-if="slot && slot.name" class="attr-slot-name" :title="slot.name">{{ slot.name }}</span>
              <button
                type="button"
                class="manual-add attr-add"
                :class="{ on: buZhangPickMode && nextBuZhangIndex === i }"
                @click="startBuZhangPick"
              >{{ buZhangPickMode && nextBuZhangIndex === i ? '点选中' : '添加' }}</button>
              <button v-if="slot && slot.code" type="button" class="attr-clear" title="取消" @click="clearBuZhang(i)">×</button>
            </div>
          </div>
          <FirstBoardPreselection
            v-if="leader"
            v-show="bottomTab === 'firstboard'"
            ref="firstBoard"
            embed-results
            board-lock="main"
            :trade-date="tradeDate"
            :embed-leader="firstBoardLeader"
            :embed-dragon-cards="dragonCards"
            :embed-dragon-selected="selectedDragonKeys"
            :embed-main-attrs="mainAttrBareNames"
            :embed-bu-zhang="buZhangSlots"
            :embed-sub-attrs="fbSubAttrs"
            :embed-pick-active="fbPreselectPickMode"
            :embed-picked-codes="fbPreselectPickedCodes"
            :embed-sync-loading="syncThsLoading"
            :embed-sync-title="boardPoolSyncTitle"
            @embed-concepts="onEmbedConcepts"
            @embed-sub-attrs="onEmbedSubAttrs"
            @embed-leader-meta="onEmbedLeaderMeta"
            @embed-pick="toggleFbPreselectFromAnalysis"
            @preselect-select-all="selectAllFbPreselect"
            @preselect-add="toggleFbPreselectPickMode"
            @sync-pools="syncBoardPoolsToThs"
          />
          <FirstBoardPreselection
            v-if="leader"
            v-show="bottomTab === 'firstboardChi'"
            ref="firstBoardChi"
            embed-results
            board-lock="chinext"
            :trade-date="tradeDate"
            :embed-leader="firstBoardLeader"
            :embed-dragon-cards="dragonCards"
            :embed-dragon-selected="selectedDragonKeys"
            :embed-main-attrs="mainAttrBareNames"
            :embed-bu-zhang="buZhangSlots"
            :embed-sub-attrs="fbSubAttrs"
            :embed-pick-active="fbChiPickMode"
            :embed-picked-codes="fbChiPickedCodes"
            :embed-sync-loading="syncThsLoading"
            :embed-sync-title="boardPoolSyncTitle"
            @embed-concepts="onEmbedConcepts"
            @embed-sub-attrs="onEmbedSubAttrs"
            @embed-leader-meta="onEmbedLeaderMeta"
            @embed-pick="toggleFbChiFromAnalysis"
            @preselect-select-all="selectAllFbChiPreselect"
            @preselect-add="toggleFbChiPickMode"
            @sync-pools="syncBoardPoolsToThs"
          />
        </div>
      </div>
    </template>
  </section>
</template>

<script>
import stockApi from '@/api/stockApi'
import GapUpAnalysis from '@/views/GapUpAnalysis/index.vue'
import FirstBoardPreselection from '@/views/FirstBoardPreselection/index.vue'
import { clearThemePropStorage } from './themePropStorage'

function isChiNextCode(code) {
  const c = normCode6(code)
  return c.startsWith('300') || c.startsWith('301')
}

function normCode6(code) {
  return String(code || '')
    .trim()
    .split('.')[0]
    .replace(/\D/g, '')
    .padStart(6, '0')
    .slice(-6)
}

function round2(n) {
  return Math.round(Number(n) * 100) / 100
}

function avgPct(rows, key) {
  let sum = 0
  let n = 0
  for (const r of rows || []) {
    const v = r && r[key]
    if (v != null && Number.isFinite(Number(v))) {
      sum += Number(v)
      n += 1
    }
  }
  return n ? round2(sum / n) : null
}

function redRatePct(rows) {
  const list = rows || []
  if (!list.length) return null
  let red = 0
  for (const r of list) {
    const v = r && r.todayChgPct
    if (v != null && Number.isFinite(Number(v)) && Number(v) > 0) red += 1
  }
  return round2((red / list.length) * 100)
}

const POOL_GROUP_SPECS = {
  gt4b: [
    { key: 'b6p', title: '≥6板', match: (r) => r.status !== '摸板未封' && Number(r.finalBoard) >= 6 },
    { key: 'b5', title: '5板', match: (r) => r.status !== '摸板未封' && Number(r.finalBoard) === 5 },
    { key: 'b4', title: '4板', match: (r) => r.status !== '摸板未封' && Number(r.finalBoard) === 4 },
    { key: 'touch', title: '摸板未封', match: (r) => r.status === '摸板未封' }
  ],
  b3: [
    { key: 'b3', title: '3板', match: (r) => r.status !== '摸板未封' && Number(r.finalBoard) >= 3 },
    { key: 'touch', title: '摸板未封', match: (r) => r.status === '摸板未封' }
  ],
  b2: [
    { key: 'b2', title: '2板', match: (r) => r.status !== '摸板未封' && Number(r.finalBoard) >= 2 },
    { key: 'touch', title: '摸板未封', match: (r) => r.status === '摸板未封' }
  ]
}

/** Flatten rows into section heads + data rows (empty sections omitted). */
function buildGroupedRows(rows, poolKey) {
  const list = rows || []
  const specs = POOL_GROUP_SPECS[poolKey] || []
  const out = []
  let idx = 0
  for (const g of specs) {
    const hit = list.filter(g.match)
    if (!hit.length) continue
    out.push({ kind: 'head', title: g.title })
    for (const row of hit) {
      idx += 1
      out.push({ kind: 'row', idx, row })
    }
  }
  return out
}

function stripThemeLabel(label) {
  return String(label || '')
    .replace(/[（(]\s*(KPL|WC|MERGE|固定)\s*[）)]\s*$/i, '')
    .trim()
}

function mapPoolApiRows(list) {
  return (list || []).map((r) => {
    const finalBoard =
      r.finalBoard != null
        ? Number(r.finalBoard)
        : r.peakBoard != null
          ? Number(r.peakBoard)
          : null
    const board =
      r.board ||
      (finalBoard != null && Number.isFinite(finalBoard) ? `${finalBoard}B` : '—')
    const nDayMBoard =
      r.nDayMBoard && String(r.nDayMBoard).trim() && String(r.nDayMBoard).trim() !== '—'
        ? String(r.nDayMBoard).trim()
        : ''
    const conceptNames = Array.isArray(r.conceptNames)
      ? r.conceptNames.map((c) => String(c || '').trim()).filter(Boolean)
      : []
    const ff =
      r.freeFloatMarketCapYi != null && Number.isFinite(Number(r.freeFloatMarketCapYi))
        ? Number(r.freeFloatMarketCapYi)
        : null
    const prem =
      r.premium12Pct != null && Number.isFinite(Number(r.premium12Pct))
        ? Number(r.premium12Pct)
        : null
    return {
      code: r.code,
      name: r.name,
      board,
      finalBoard: Number.isFinite(finalBoard) ? finalBoard : null,
      nDayMBoard,
      conceptNames,
      freeFloatMarketCapYi: ff,
      premium12Pct: prem,
      totalReturnPct: r.totalReturnPct,
      todayChgPct: r.todayChgPct != null ? r.todayChgPct : r.followPct,
      status: r.status || ''
    }
  })
}

function emptyPoolBlock() {
  return { count: 0, rows: [], totalReturnPct: null, todayRedRatePct: null }
}

/** v3：只跟龙头；换日保留，退池清掉。旧版 v2=日+龙，v1=日+龙+题材 */
const MAIN_ATTR_LS_PREFIX = 'lcc.themeProp.mainAttr.v1'
const BUZHANG_LS_PREFIX = 'lcc.themeProp.buZhang.v1'
const FB_LEADER_LS_PREFIX = 'lcc.themeProp.fbLeader.v1'
const FB_SUB_LS_PREFIX = 'lcc.themeProp.fbSub.v1'
const FB_CONCEPT_LS_PREFIX = 'lcc.themeProp.fbConcepts.v1'
const FB_SUB_SLOTS = [
  { key: 'attrSub1', label: '副属性1' },
  { key: 'attrSub2', label: '副属性2' },
  { key: 'attrSub3', label: '副属性3' }
]
const HEIGHT_LS_PREFIX = 'lcc.themeProp.heightManual.v3'
const HEIGHT_LS_PREFIX_V2 = 'lcc.themeProp.heightManual.v2'
const HEIGHT_LS_PREFIX_V1 = 'lcc.themeProp.heightManual.v1'
/** 接力 TOP 池 v2：只跟龙头，不跟日期。旧 v1 带交易日。 */
const RELAY_LS_PREFIX = 'lcc.themeProp.relayTop.v3'
const FB_PRESELECT_LS_PREFIX = 'lcc.themeProp.fbPreselect.v1'
const FB_CHI_PRESELECT_LS_PREFIX = 'lcc.themeProp.fbPreselectChi.v1'

function emptyRelayRow() {
  return {
    id: `r-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    code: '',
    name: '',
    midChip: null,
    rangeAllD0: null,
    marketValue: null,
    boardLabel: '',
    conceptScoreSum: null,
    matchedConcepts: []
  }
}

function finiteOrNull(v) {
  if (v == null || v === '') return null
  const n = Number(v)
  return Number.isFinite(n) ? n : null
}

function emptyHeightRow() {
  return {
    id: `h-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    code: '',
    name: '',
    lianBanHeight: '',
    finalBoardLabel: '',
    totalReturnText: '',
    freeFloatMarketCapYi: null,
    premium12Pct: null,
    conceptNames: []
  }
}

function formatReturnTextFromPct(v) {
  if (v == null || !Number.isFinite(Number(v))) return ''
  const n = Math.round(Number(v) * 100) / 100
  const sign = n > 0 ? '+' : ''
  return sign + n
}

function sortHeightRowsByBoard(list) {
  return (list || []).slice().sort((a, b) => {
    const av = parseBoardHeight(a && a.lianBanHeight)
    const bv = parseBoardHeight(b && b.lianBanHeight)
    const an = av == null ? -1 : av
    const bn = bv == null ? -1 : bv
    return bn - an
  })
}

/** Parse board height from free text: 5 / 5B / 5板 / 7天6板 → prefer trailing 板 number for N天M板 */
function parseBoardHeight(text) {
  const s = String(text || '').trim()
  if (!s) return null
  const nDayM = s.match(/(\d+)\s*天\s*(\d+)\s*板/)
  if (nDayM) {
    const m = Number(nDayM[2])
    return Number.isFinite(m) && m > 0 ? m : null
  }
  const xb = s.match(/^(\d+)\s*[Bb板]?$/)
  if (xb) {
    const n = Number(xb[1])
    return Number.isFinite(n) && n > 0 ? n : null
  }
  const any = s.match(/(\d+)\s*[Bb板]/)
  if (any) {
    const n = Number(any[1])
    return Number.isFinite(n) && n > 0 ? n : null
  }
  const n = Number(s)
  return Number.isFinite(n) && n > 0 ? n : null
}

function parseReturnPct(text) {
  const s = String(text == null ? '' : text).trim().replace(/%/g, '')
  if (!s) return null
  const n = Number(s)
  return Number.isFinite(n) ? n : null
}

export default {
  name: 'ThemePropCenter',
  components: { GapUpAnalysis, FirstBoardPreselection },
  props: {
    leader: { type: Object, default: null },
    leaders: { type: Array, default: () => [] },
    conceptsByCode: { type: Object, default: () => ({}) },
    limitUpRows: { type: Array, default: () => [] },
    pack: { type: Object, required: true },
    tradeDate: { type: String, default: '' },
    hideStockHead: { type: Boolean, default: false },
    rolePick: { type: String, default: '' },
    /** 板块 TAB。有值时确认池跟板块走，不跟下方点选的题材走 */
    sectorKey: { type: String, default: '' },
    sectorConcept: { type: String, default: '' },
    sectorSource: { type: String, default: '' },
    kingdomName: { type: String, default: '' },
    syncDragons: { type: Array, default: () => [] },
    dragonCards: { type: Array, default: () => [] },
    savedMainAttrs: { type: Array, default: () => [] }
  },
  mounted() {
    document.addEventListener('click', this.onDocClickCloseFbMenu)
    this.pushHeightBar()
    this.loadVeteranConfirm()
    this.loadConfirmPools()
    this.loadThemeWindowReturns()
    if (this.leader) this.catchupPaneReady = true
  },
  beforeDestroy() {
    document.removeEventListener('click', this.onDocClickCloseFbMenu)
  },
  data() {
    return {
      selectedTheme: '',
      bottomTab: 'theme',
      veteranPane: 'confirm',
      veteranChecked: {},
      veteranConfirmed: {},
      veteranSnapshots: {},
      confirmSeq: 0,
      confirmLoading: false,
      confirmPools: {
        gt4b: emptyPoolBlock(),
        b3: emptyPoolBlock(),
        b2: emptyPoolBlock()
      },
      catchupPaneReady: false,
      conceptDialogVisible: false,
      themeExpanded: false,
      themePreviewN: 15,
      sortKey: 'sinceLeaderPct',
      sortDesc: true,
      once4bSeq: 0,
      once4bLoading: false,
      oncePools: {
        gt4b: emptyPoolBlock(),
        b3: emptyPoolBlock(),
        b2: emptyPoolBlock()
      },
      once4bMeta: null,
      onceFreeFloatByCode: {},
      oncePremium12ByCode: {},
      leaderSinceReturnPct: null,
      heightManualRows: [],
      heightBoardTab: 'height',
      themeGainWindows: { 10: [], 15: [], 20: [] },
      themeGainLoading: false,
      themeGainSeq: 0,
      heightMoodByCode: {},
      heightMoodSeq: 0,
      heightStorageKey: '',
      heightPickMode: false,
      relayTopRows: [],
      relayMetaSeq: 0,
      relayStorageKey: '',
      relayPickMode: false,
      relayAwaitMainAttr: false,
      upperPool: 'relay',
      fbPreselectRows: [],
      fbPreselectStorageKey: '',
      fbPreselectPickMode: false,
      fbChiPreselectRows: [],
      fbChiPreselectStorageKey: '',
      fbChiPickMode: false,
      syncThsLoading: false,
      selectedDragonKeys: [],
      mainAttrs: ['', '', ''],
      mainAttrPickMode: false,
      buZhangSlots: [null, null, null],
      buZhangPickMode: false,
      leaderPickMode: false,
      fbLeaderOverride: null,
      fbConcepts: [],
      fbSubAttrs: { attrSub1: '', attrSub2: '', attrSub3: '' },
      fbSubSlotDefs: FB_SUB_SLOTS,
      fbLeaderMeta: { priceText: '—', ffText: '—', title: '' },
      fbAttrMenu: { visible: false, x: 0, y: 0, concept: '' }
    }
  },
  watch: {
    mainAttrs: {
      deep: true,
      handler() {
        this.pushHeightBar()
        this.loadThemeWindowReturns()
      }
    },
    savedMainAttrs: {
      immediate: true,
      handler(list) {
        const incoming = (Array.isArray(list) ? list : []).map((item) => String(item || '').trim()).slice(0, 3)
        while (incoming.length < 3) incoming.push('')
        if (this._suppressSavedMainAttrs) {
          if (!incoming.some(Boolean)) this._suppressSavedMainAttrs = false
          return
        }
        if (!incoming.some(Boolean)) return
        const localHas = (this.mainAttrs || []).some((item) => String(item || '').trim())
        if (localHas) return
        this.mainAttrs = incoming
        this.persistMainAttrs()
      }
    },
    dragonCards: {
      immediate: true,
      handler(list) {
        const cards = Array.isArray(list) ? list : []
        const valid = new Set(cards.map((card) => card && card.key).filter(Boolean))
        const kept = (this.selectedDragonKeys || []).filter((key) => valid.has(key))
        if (kept.length) {
          if (kept.length !== (this.selectedDragonKeys || []).length) this.selectedDragonKeys = kept
          return
        }
        this.selectedDragonKeys = cards.filter((card) => card && card.role === 'leader').map((card) => card.key)
      }
    },
    mainAttrPickMode() {
      this.pushHeightBar()
    },
    nonLeaderMaxHeightLabel() {
      this.pushHeightBar()
    },
    heightPickMode() {
      this.pushHeightBar()
    },
    heightManualRows: {
      deep: true,
      handler() {
        this.loadHeightMood()
      }
    },
    leaders() {
      this.syncHeightRowsFromPools()
    },
    conceptsByCode: {
      deep: true,
      handler() {
        this.syncHeightRowsFromPools()
      }
    },
    limitUpRows() {
      this.syncHeightRowsFromPools()
    },
    pack: {
      immediate: true,
      handler(p) {
        const names = (p && p.concepts) || []
        if (!names.includes(this.selectedTheme)) this.selectedTheme = names[0] || ''
        this.themeExpanded = false
        this.$nextTick(() => {
          this.loadLeaderSaved()
        })
      }
    },
    leader(next, prev) {
      const nc = next ? normCode6(next.code || next.leaderId) : ''
      const pc = prev ? normCode6(prev.code || prev.leaderId) : ''
      if (nc) this.catchupPaneReady = true
      if (nc && nc === pc) {
        this.maybeClearIfExited(next)
        return
      }
      this.relayAwaitMainAttr = false
      this.themeExpanded = false
      this.heightPickMode = false
      this.relayPickMode = false
      this.fbPreselectPickMode = false
      this.fbChiPickMode = false
      this.mainAttrPickMode = false
      this.buZhangPickMode = false
      this.leaderPickMode = false
      this.closeFbAttrMenu()
      this.resetOnce4b()
      this.loadLeaderSaved()
      this.loadVeteranConfirm()
      this.maybeClearIfExited(next)
      this.$nextTick(() => {
        this.loadOnce4b()
        this.loadConfirmPools()
        this.loadHeightMood()
      })
    },
    tradeDate() {
      this.heightPickMode = false
      this.relayPickMode = false
      this.fbPreselectPickMode = false
      this.fbChiPickMode = false
      this.mainAttrPickMode = false
      this.buZhangPickMode = false
      this.leaderPickMode = false
      this.closeFbAttrMenu()
      this.resetOnce4b()
      // 主属性、补涨概念、高度名单跟龙头。接力 TOP 池按查看日分开，换日只读这一天。
      this.maybeClearIfExited(this.leader)
      this.loadRelayRows()
      this.loadFbPreselectRows()
      this.loadFbChiPreselectRows()
      this.$nextTick(() => {
        this.loadOnce4b()
        this.loadConfirmPools()
        this.loadHeightMood()
        this.loadThemeWindowReturns()
      })
    },
    sectorKey() {
      this.loadVeteranConfirm()
      this.loadConfirmPools()
    },
    sectorConcept() {
      this.loadConfirmPools()
    },
    sectorSource() {
      this.loadConfirmPools()
    },
    selectedTheme(name) {
      this.heightPickMode = false
      if (name && !this.themeExpanded) {
        const idx = this.rankedRows.findIndex((r) => r && r.name === name)
        if (idx >= this.themePreviewN) this.themeExpanded = true
      }
      this.loadOnce4b()
    },
    'pack.anchorDate'() {
      this.loadOnce4b()
      this.loadConfirmPools()
    }
  },
  computed: {
    activeThemeGainRows() {
      const span = this.heightBoardTab === 'd10' ? 10 : this.heightBoardTab === 'd15' ? 15 : this.heightBoardTab === 'd20' ? 20 : 0
      if (!span) return []
      const windows = this.themeGainWindows || {}
      const rows = windows[span] || windows[String(span)] || []
      return Array.isArray(rows) ? rows : []
    },
    themeGainEmptyText() {
      const hasAttr = (this.mainAttrs || []).some((item) => stripThemeLabel(item))
      if (!hasAttr) return '先在上方添加主属性，再看这几天涨幅前 10'
      return '这几个主属性下没有可统计的涨幅'
    },
    nonLeaderMaxHeightLabel() {
      let max = null
      for (const row of this.heightManualRows || []) {
        const n = parseBoardHeight(row && row.lianBanHeight)
        if (n == null) continue
        if (max == null || n > max) max = n
      }
      return max != null ? `${max}B` : '—'
    },
    heightPickedCodeSet() {
      const set = {}
      for (const row of this.heightManualRows || []) {
        const c = normCode6(row && row.code)
        if (c && c !== '000000') set[c] = true
      }
      return set
    },
    relayPickedCodes() {
      return (this.relayTopRows || []).map((r) => normCode6(r && r.code)).filter((c) => c && c !== '000000')
    },
    fbPreselectPickedCodes() {
      return (this.fbPreselectRows || []).map((r) => normCode6(r && r.code)).filter((c) => c && c !== '000000')
    },
    fbChiPickedCodes() {
      return (this.fbChiPreselectRows || []).map((r) => normCode6(r && r.code)).filter((c) => c && c !== '000000')
    },
    activeFbPreselectRows() {
      return this.upperPool === 'fbchi' ? this.fbChiPreselectRows : this.fbPreselectRows
    },
    /** 龙头首板以来题材涨幅 Top10（裸名，用于概念列过滤/粉底） */
    top10ThemeBareNames() {
      return (this.rankedRows || []).slice(0, 10).map((r) => stripThemeLabel(r.name)).filter(Boolean)
    },
    /** 手动名单内概念出现率：count/名单人数 */
    heightConceptAppearRate() {
      const rows = this.heightManualRows || []
      const n = rows.length
      const rates = {}
      if (!n) return rates
      const counts = {}
      for (const row of rows) {
        const seen = new Set()
        for (const raw of row.conceptNames || []) {
          const c = String(raw || '').trim()
          if (!c || seen.has(c)) continue
          seen.add(c)
          counts[c] = (counts[c] || 0) + 1
        }
      }
      for (const [c, cnt] of Object.entries(counts)) {
        rates[c] = (cnt / n) * 100
      }
      return rates
    },
    rankedRows() {
      const rows = (this.pack && this.pack.themeRows) || []
      if (this.pack && (this.pack.loadingConcepts || this.pack.loadingReturns)) return []
      const key = this.sortKey || 'sinceLeaderPct'
      const dir = this.sortDesc ? -1 : 1
      return rows
        .filter((r) => r && r.name)
        .slice()
        .sort((a, b) => {
          const av = a[key]
          const bv = b[key]
          const aMiss = av == null || !Number.isFinite(Number(av))
          const bMiss = bv == null || !Number.isFinite(Number(bv))
          if (aMiss && bMiss) return String(a.name || '').localeCompare(String(b.name || ''))
          if (aMiss) return 1
          if (bMiss) return -1
          const diff = (Number(av) - Number(bv)) * dir
          if (diff !== 0) return diff
          return Number(b.sinceLeaderPct) - Number(a.sinceLeaderPct)
        })
    },
    visibleRows() {
      const rows = this.rankedRows
      if (this.mainAttrPickMode || this.themeExpanded || rows.length <= this.themePreviewN) return rows
      return rows.slice(0, this.themePreviewN)
    },
    nextMainAttrIndex() {
      return (this.mainAttrs || []).findIndex((s) => !String(s || '').trim())
    },
    nextBuZhangIndex() {
      return (this.buZhangSlots || []).findIndex((s) => !(s && s.code))
    },
    firstBoardLeader() {
      const pick = this.fbLeaderOverride
      if (pick && pick.code) return { code: pick.code, name: pick.name, leaderId: pick.code }
      return this.leader
    },
    firstBoardLeaderName() {
      const pick = this.fbLeaderOverride
      if (pick && pick.name) return pick.name
      return (this.leader && this.leader.name) || '未选'
    },
    mainAttrBareNames() {
      const seen = new Set()
      const out = []
      for (const raw of this.mainAttrs || []) {
        const bare = stripThemeLabel(raw)
        if (!bare || seen.has(bare)) continue
        seen.add(bare)
        out.push(bare)
      }
      return out
    },
    hiddenThemeCount() {
      if (this.mainAttrPickMode) return 0
      return Math.max(0, this.rankedRows.length - this.themePreviewN)
    },
    priceDelta() {
      const close = Number(this.leader && this.leader.closePrice)
      const pct = Number(this.leader && this.leader.changePct)
      if (!Number.isFinite(close) || !Number.isFinite(pct) || pct <= -100) return null
      const prev = close / (1 + pct / 100)
      return Math.round((close - prev) * 100) / 100
    },
    leaderSinceDisplay() {
      if (this.leaderSinceReturnPct != null && Number.isFinite(Number(this.leaderSinceReturnPct))) {
        return Number(this.leaderSinceReturnPct)
      }
      const p = this.pack && this.pack.sinceLeaderReturnPct
      return p != null && Number.isFinite(Number(p)) ? Number(p) : null
    },
    thsSyncTitle() {
      const labels = this.thsGroupLabels
      return `写入顺序「${labels.firstBoardChi}」「${labels.firstBoard}」「${labels.veteran}」「${labels.relay}」，最后空分组「${labels.separator}」。首板和首板创业有票时带总龙、趋势龙。池子为空则跳过。五个账号同时写。`
    },
    boardPoolSyncTitle() {
      return this.thsSyncTitle
    },
    thsGroupLabels() {
      const name = String((this.leader && this.leader.name) || '').replace(/\s+/g, '')
      const short = [...name].slice(0, 2).join('') || '未知'
      const yi = '\u4e00'
      const kingdom = String(this.kingdomName || '').replace(/\s+/g, '')
      return {
        relay: `${short}${yi}接力TOP`,
        veteran: `${short}${yi}老臣`,
        firstBoard: `${short}${yi}首板`,
        firstBoardChi: `${short}${yi}首板创业`,
        separator: kingdom ? `${yi}${yi}${kingdom}${yi}${yi}` : ''
      }
    },
    poolCols() {
      const sectorConfirm = this.veteranPane === 'confirm' && !!this.sectorKey
      const loading = sectorConfirm ? this.confirmLoading : this.once4bLoading
      const pools = sectorConfirm ? this.confirmPools : this.oncePools
      const spec = [
        { key: 'gt4b', title: '曾≥4B池', tone: 'blue' },
        { key: 'b3', title: '曾 3B池', tone: 'orange' },
        { key: 'b2', title: '曾 2B池', tone: 'green' }
      ]
      return spec.map((s) => {
        const block = (pools && pools[s.key]) || emptyPoolBlock()
        let rows = block.rows || []
        if (this.veteranPane === 'confirm') {
          rows = rows.filter((row) => this.veteranConfirmed[normCode6(row && row.code)])
          if (sectorConfirm) rows = this.mergeConfirmRows(s.key, rows)
        }
        const confirmedView = this.veteranPane === 'confirm'
        return {
          ...s,
          count: loading ? '…' : (confirmedView ? rows.length : (block.count != null ? block.count : rows.length)),
          totalReturnPct: loading ? null : (confirmedView ? avgPct(rows, 'totalReturnPct') : block.totalReturnPct),
          todayRedRatePct: loading ? null : (confirmedView ? redRatePct(rows) : block.todayRedRatePct),
          displayRows: loading ? [] : buildGroupedRows(rows, s.key)
        }
      })
    }
  },
  methods: {
    codesInOrder(rows) {
      const out = []
      const seen = new Set()
      for (const raw of rows || []) {
        const code = normCode6(raw && raw.code)
        if (!code || code === '000000' || seen.has(code)) continue
        seen.add(code)
        out.push(code)
      }
      return out
    },
    poolCodesInDisplayOrder(poolKey) {
      const col = (this.poolCols || []).find((c) => c.key === poolKey)
      const out = []
      for (const item of (col && col.displayRows) || []) {
        if (!item || item.kind !== 'row' || !item.row) continue
        out.push(item.row)
      }
      return this.codesInOrder(out)
    },
    async syncThemePropToThs() {
      if (this.syncThsLoading) return
      if ((this.veteranPane === 'confirm' && this.sectorKey) ? this.confirmLoading : this.once4bLoading) {
        this.$message.warning('补涨池还在加载，请稍后再同步')
        return
      }
      const leaderName = String((this.leader && this.leader.name) || '').trim()
      const leaderCode = normCode6(this.leader && (this.leader.code || this.leader.leaderId))
      const kingdomName = String(this.kingdomName || '').replace(/\s+/g, '')
      if (!leaderName || !leaderCode || leaderCode === '000000') {
        this.$message.warning('请先选择龙头')
        return
      }
      if (!kingdomName) {
        this.$message.warning('请先进入王国再同步')
        return
      }
      const relayCodes = this.codesInOrder(this.relayTopRows)
      const seen = new Set()
      const veteranCodes = []
      const pushVeteran = (codes) => {
        for (const code of codes || []) {
          if (!code || seen.has(code)) continue
          seen.add(code)
          veteranCodes.push(code)
        }
      }
      pushVeteran(this.codesInOrder(this.heightManualRows))
      pushVeteran(this.poolCodesInDisplayOrder('gt4b'))
      pushVeteran(this.poolCodesInDisplayOrder('b3'))
      pushVeteran(this.poolCodesInDisplayOrder('b2'))
      const firstBoardCodes = this.codesInOrder(this.fbPreselectRows)
      const firstBoardChiCodes = this.codesInOrder(this.fbChiPreselectRows).filter((code) => isChiNextCode(code))
      const dragonCodes = (this.syncDragons || []).map((row) => normCode6(row && row.code)).filter((code) => code && code !== '000000')
      this.syncThsLoading = true
      try {
        const res = await stockApi.syncThemePropToThs(
          leaderName,
          relayCodes,
          veteranCodes,
          leaderCode,
          kingdomName,
          firstBoardCodes,
          firstBoardChiCodes,
          dragonCodes
        )
        if (res?.authError) {
          this.$message.warning(res?.message || '同花顺 Cookie 已失效，请更新 THS_COOKIE')
          return
        }
        if (res?.thsEnabled === false) {
          this.$message.warning(res?.message || '未配置 THS_COOKIE')
          return
        }
        if (res?.partial) {
          this.$message.warning(res?.message || '部分账号同步失败')
          return
        }
        if (!res?.ok) {
          this.$message.error(res?.message || '同步失败')
          return
        }
        this.$message.success(res?.message || '同步成功')
      } catch (_error) {
        this.$message.error('同步失败')
      } finally {
        this.syncThsLoading = false
      }
    },
    async syncBoardPoolsToThs() {
      return this.syncThemePropToThs()
    },
    setBottomTab(tab) {
      this.bottomTab = tab
      if (tab === 'theme') this.veteranPane = 'preselect'
      if (tab === 'catchup') {
        this.catchupPaneReady = true
        this.$nextTick(() => {
          const gap = this.$refs.catchupGap
          const table = gap && gap.$refs && gap.$refs.limitUpTable
          if (table && typeof table.doLayout === 'function') table.doLayout()
        })
        return
      }
      if (tab !== 'firstboard' && tab !== 'firstboardChi') return
      this.$nextTick(() => {
        const fb = tab === 'firstboardChi' ? this.$refs.firstBoardChi : this.$refs.firstBoard
        const table = fb && fb.$refs && fb.$refs.resultTable
        if (table && typeof table.doLayout === 'function') table.doLayout()
      })
    },
    veteranConfirmKey() {
      if (this.sectorKey) return `lcc.themeProp.veteranConfirm.v1|sector|${this.sectorKey}`
      const code = this.leader ? normCode6(this.leader.code || this.leader.leaderId) : ''
      if (!code || code === '000000') return ''
      return `lcc.themeProp.veteranConfirm.v1|${code}`
    },
    leaderVeteranKey() {
      const code = this.leader ? normCode6(this.leader.code || this.leader.leaderId) : ''
      if (!code || code === '000000') return ''
      return `lcc.themeProp.veteranConfirm.v1|${code}`
    },
    readVeteranStore(key) {
      if (!key) return null
      try {
        const rawText = localStorage.getItem(key)
        if (rawText == null) return null
        const raw = JSON.parse(rawText)
        if (Array.isArray(raw)) return { codes: raw, rows: {} }
        if (raw && Array.isArray(raw.codes)) return { codes: raw.codes, rows: raw.rows && typeof raw.rows === 'object' ? raw.rows : {} }
      } catch (err) {
        console.warn('[theme-prop] veteran confirm read failed', err)
      }
      return { codes: [], rows: {} }
    },
    loadVeteranConfirm() {
      const confirmed = {}
      const snapshots = {}
      let stored = this.readVeteranStore(this.veteranConfirmKey())
      if (stored == null && this.sectorKey) stored = this.readVeteranStore(this.leaderVeteranKey())
      if (stored) {
        (stored.codes || []).forEach((code) => {
          const c = normCode6(code)
          if (c && c !== '000000') confirmed[c] = true
        })
        Object.keys(stored.rows || {}).forEach((code) => {
          const c = normCode6(code)
          const row = stored.rows[code]
          if (c && row) snapshots[c] = row
        })
      }
      this.veteranConfirmed = confirmed
      this.veteranChecked = { ...confirmed }
      this.veteranSnapshots = snapshots
    },
    isVeteranChecked(code) {
      return !!this.veteranChecked[normCode6(code)]
    },
    toggleVeteranCheck(code, event) {
      const c = normCode6(code)
      if (!c) return
      const on = event && event.target ? !!event.target.checked : !this.veteranChecked[c]
      if (on) this.$set(this.veteranChecked, c, true)
      else this.$delete(this.veteranChecked, c)
    },
    saveVeteranChecked() {
      const key = this.veteranConfirmKey()
      if (!key) {
        this.$message.warning(this.sectorKey ? '请先打开板块' : '请先选择龙头')
        return
      }
      const codes = Object.keys(this.veteranChecked).filter((code) => this.veteranChecked[code])
      const confirmed = {}
      const snapshots = {}
      codes.forEach((code) => { confirmed[code] = true })
      for (const poolKey of ['gt4b', 'b3', 'b2']) {
        const rows = (this.oncePools && this.oncePools[poolKey] && this.oncePools[poolKey].rows) || []
        rows.forEach((row) => {
          const c = normCode6(row && row.code)
          if (!c || !confirmed[c]) return
          snapshots[c] = { ...row, poolKey }
        })
      }
      codes.forEach((code) => {
        if (!snapshots[code] && this.veteranSnapshots[code]) snapshots[code] = this.veteranSnapshots[code]
      })
      this.veteranConfirmed = confirmed
      this.veteranSnapshots = snapshots
      try {
        localStorage.setItem(key, JSON.stringify({ codes, rows: snapshots }))
      } catch (err) {
        console.warn('[theme-prop] veteran confirm save failed', err)
      }
      this.veteranPane = 'confirm'
      this.$message.success(codes.length ? `已放入确认池 ${codes.length} 只` : '确认池已清空')
    },
    clearVeteranPool() {
      const key = this.veteranConfirmKey()
      if (!key) {
        this.$message.warning(this.sectorKey ? '请先打开板块' : '请先选择龙头')
        return
      }
      this.veteranConfirmed = {}
      this.veteranChecked = {}
      this.veteranSnapshots = {}
      try {
        localStorage.setItem(key, JSON.stringify({ codes: [], rows: {} }))
      } catch (err) {
        console.warn('[theme-prop] veteran clear failed', err)
      }
      this.veteranPane = 'confirm'
      this.$message.success('老臣池已清空')
    },
    mergeConfirmRows(poolKey, liveRows) {
      const out = (liveRows || []).slice()
      const seen = new Set(out.map((row) => normCode6(row && row.code)).filter(Boolean))
      Object.keys(this.veteranConfirmed || {}).forEach((code) => {
        if (seen.has(code)) return
        const snap = this.veteranSnapshots && this.veteranSnapshots[code]
        if (!snap || snap.poolKey !== poolKey) return
        out.push(snap)
        seen.add(code)
      })
      return out
    },
    loadLeaderSaved() {
      this.loadHeightRows()
      this.loadRelayRows()
      this.loadFbPreselectRows()
      this.loadFbChiPreselectRows()
      this.loadMainAttrs()
      this.loadBuZhangSlots()
      this.loadFbLeaderOverride()
      this.loadFbSubAttrs()
      this.loadFbConcepts()
    },
    maybeClearIfExited(leader) {
      if (!leader) return
      const exitDate = String(leader.exitDate || '').trim()
      const td = String(this.tradeDate || '').trim()
      if (!exitDate || !td || exitDate > td) return
      const code = normCode6(leader.code || leader.leaderId)
      if (!code || code === '000000') return
      clearThemePropStorage(code)
      this.mainAttrs = ['', '', '']
      this.buZhangSlots = [null, null, null]
      this.fbLeaderOverride = null
      this.heightManualRows = []
      this.relayTopRows = []
      this.fbPreselectRows = []
      this.fbChiPreselectRows = []
      this.fbConcepts = []
      this.fbSubAttrs = { attrSub1: '', attrSub2: '', attrSub3: '' }
    },
    fbSubLsKey() {
      const code = this.leader ? normCode6(this.leader.code || this.leader.leaderId) : ''
      if (!code || code === '000000') return ''
      return `${FB_SUB_LS_PREFIX}|${code}`
    },
    loadFbSubAttrs() {
      const empty = { attrSub1: '', attrSub2: '', attrSub3: '' }
      const key = this.fbSubLsKey()
      if (!key) return
      try {
        const raw = JSON.parse(localStorage.getItem(key) || 'null')
        this.fbSubAttrs = {
          attrSub1: String((raw && raw.attrSub1) || '').trim(),
          attrSub2: String((raw && raw.attrSub2) || '').trim(),
          attrSub3: String((raw && raw.attrSub3) || '').trim()
        }
      } catch (err) {
        this.fbSubAttrs = empty
      }
    },
    persistFbSubAttrs() {
      const key = this.fbSubLsKey()
      if (!key) return
      try {
        localStorage.setItem(key, JSON.stringify({
          attrSub1: this.fbSubAttrs.attrSub1 || '',
          attrSub2: this.fbSubAttrs.attrSub2 || '',
          attrSub3: this.fbSubAttrs.attrSub3 || ''
        }))
      } catch (err) {
        console.warn('[theme-prop] fb sub save failed', err)
      }
    },
    fbConceptLsKey() {
      const code = this.leader ? normCode6(this.leader.code || this.leader.leaderId) : ''
      if (!code || code === '000000') return ''
      return `${FB_CONCEPT_LS_PREFIX}|${code}`
    },
    loadFbConcepts() {
      const key = this.fbConceptLsKey()
      if (!key) return
      try {
        const raw = JSON.parse(localStorage.getItem(key) || '[]')
        const list = Array.isArray(raw) ? raw : []
        this.fbConcepts = list
          .map((item) => ({
            name: String((item && item.name) || '').trim(),
            count: Number(item && item.count)
          }))
          .filter((item) => item.name && item.count >= 2)
      } catch (err) {
        this.fbConcepts = []
      }
    },
    persistFbConcepts() {
      const key = this.fbConceptLsKey()
      if (!key) return
      try {
        const list = (this.fbConcepts || [])
          .map((item) => ({
            name: String((item && item.name) || '').trim(),
            count: Number(item && item.count)
          }))
          .filter((item) => item.name && item.count >= 2)
        localStorage.setItem(key, JSON.stringify(list))
      } catch (err) {
        console.warn('[theme-prop] fb concepts save failed', err)
      }
    },
    parseReturnPct,
    attrLabel(raw) {
      return stripThemeLabel(raw)
    },
    pushHeightBar() {
      if (!this.hideStockHead) return
      const attrs = (this.mainAttrs || []).map((item) => (item ? this.attrLabel(item) : ''))
      const attrRaws = (this.mainAttrs || []).map((item) => String(item || '').trim())
      this.$emit('height-bar', {
        height: this.nonLeaderMaxHeightLabel || '—',
        attrs,
        attrRaws,
        pickMode: !!this.mainAttrPickMode,
        nextIndex: this.nextMainAttrIndex,
        heightListPick: !!this.heightPickMode
      })
    },
    mainAttrIndex(name) {
      const bare = stripThemeLabel(name)
      if (!bare) return -1
      return (this.mainAttrs || []).findIndex((s) => stripThemeLabel(s) === bare)
    },
    mainAttrLsKey() {
      const code = this.leader ? normCode6(this.leader.code || this.leader.leaderId) : ''
      if (!code || code === '000000') return ''
      return `${MAIN_ATTR_LS_PREFIX}|${code}`
    },
    loadMainAttrs() {
      const key = this.mainAttrLsKey()
      const empty = ['', '', '']
      if (!key) {
        this.mainAttrs = empty
        return
      }
      try {
        const raw = JSON.parse(localStorage.getItem(key) || 'null')
        const list = Array.isArray(raw) ? raw.map((s) => String(s || '').trim()).slice(0, 3) : []
        while (list.length < 3) list.push('')
        const adopted = list.some(Boolean) ? list : this.savedMainAttrList(empty)
        this.mainAttrs = adopted
        if (!list.some(Boolean) && adopted.some(Boolean)) this.persistMainAttrs()
      } catch (err) {
        this.mainAttrs = this.savedMainAttrList(empty.slice())
      }
    },
    savedMainAttrList(fallback) {
      const incoming = (this.savedMainAttrs || []).map((item) => String(item || '').trim()).slice(0, 3)
      while (incoming.length < 3) incoming.push('')
      return incoming.some(Boolean) ? incoming : fallback
    },
    persistMainAttrs() {
      const key = this.mainAttrLsKey()
      if (!key) return
      try {
        const list = (this.mainAttrs || []).slice(0, 3).map((s) => String(s || '').trim())
        while (list.length < 3) list.push('')
        localStorage.setItem(key, JSON.stringify(list))
      } catch (err) {
        console.warn('[theme-prop] main attr save failed', err)
      }
    },
    startMainAttrPick() {
      this.mainAttrPickMode = true
      this.heightPickMode = false
      this.relayPickMode = false
      this.fbPreselectPickMode = false
      this.fbChiPickMode = false
      this.buZhangPickMode = false
      this.leaderPickMode = false
      this.setBottomTab('theme')
      this.$nextTick(() => {
        const el = this.$el && this.$el.querySelector && this.$el.querySelector('.theme-table')
        if (el && el.scrollIntoView) el.scrollIntoView({ block: 'nearest' })
      })
    },
    onThemeRowClick(row) {
      if (!row) return
      if (this.mainAttrPickMode) {
        this.assignMainAttr(row.name)
        return
      }
      this.selectedTheme = row.name
    },
    assignMainAttr(label) {
      const name = String(label || '').trim()
      const bare = stripThemeLabel(name)
      if (!bare) return
      const slots = (this.mainAttrs || ['', '', '']).slice(0, 3)
      while (slots.length < 3) slots.push('')
      const idx = slots.findIndex((s) => stripThemeLabel(s) === bare)
      if (idx >= 0) {
        slots[idx] = ''
      } else {
        const empty = slots.findIndex((s) => !String(s || '').trim())
        if (empty < 0) {
          this.$message.warning('主属性已选满 3 个，先取消一个再选')
          return
        }
        slots[empty] = name
        if (!slots.some((s) => !String(s || '').trim())) this.mainAttrPickMode = false
      }
      this.mainAttrs = slots
      this.persistMainAttrs()
      if (idx >= 0) {
        this.relayAwaitMainAttr = true
        this.clearRelayPool()
        return
      }
      this.relayAwaitMainAttr = false
    },
    clearAllMainAttrs() {
      this._suppressSavedMainAttrs = true
      this.mainAttrs = ['', '', '']
      this.mainAttrPickMode = false
      this.persistMainAttrs()
      this.pushHeightBar()
    },
    clearMainAttr(index) {
      const slots = (this.mainAttrs || ['', '', '']).slice(0, 3)
      while (slots.length < 3) slots.push('')
      if (index < 0 || index >= slots.length) return
      slots[index] = ''
      this.mainAttrs = slots
      this.persistMainAttrs()
      this.relayAwaitMainAttr = true
      this.clearRelayPool()
    },
    clearRelayPool() {
      this.relayTopRows = []
      this.persistRelayRows()
    },
    buZhangLsKey() {
      const code = this.leader ? normCode6(this.leader.code || this.leader.leaderId) : ''
      if (!code || code === '000000') return ''
      return `${BUZHANG_LS_PREFIX}|${code}`
    },
    loadBuZhangSlots() {
      const empty = [null, null, null]
      const key = this.buZhangLsKey()
      if (!key) {
        this.buZhangSlots = empty
        return
      }
      try {
        const raw = JSON.parse(localStorage.getItem(key) || 'null')
        const list = Array.isArray(raw) ? raw.slice(0, 3) : []
        const slots = [null, null, null]
        list.forEach((item, index) => {
          const code = normCode6(item && item.code)
          const name = String((item && item.name) || '').trim()
          if (!code || code === '000000' || !name) return
          slots[index] = { code, name }
        })
        this.buZhangSlots = slots
      } catch (err) {
        this.buZhangSlots = empty
      }
    },
    fbLeaderLsKey() {
      const code = this.leader ? normCode6(this.leader.code || this.leader.leaderId) : ''
      if (!code || code === '000000') return ''
      return `${FB_LEADER_LS_PREFIX}|${code}`
    },
    loadFbLeaderOverride() {
      const key = this.fbLeaderLsKey()
      if (!key) {
        this.fbLeaderOverride = null
        return
      }
      try {
        const raw = JSON.parse(localStorage.getItem(key) || 'null')
        const code = normCode6(raw && raw.code)
        const name = String((raw && raw.name) || '').trim()
        const card = normCode6(this.leader && (this.leader.code || this.leader.leaderId))
        if (!code || code === '000000' || !name || code === card) {
          this.fbLeaderOverride = null
          return
        }
        this.fbLeaderOverride = { code, name }
      } catch (err) {
        this.fbLeaderOverride = null
      }
    },
    persistFbLeaderOverride() {
      const key = this.fbLeaderLsKey()
      if (!key) return
      try {
        if (!this.fbLeaderOverride) localStorage.removeItem(key)
        else localStorage.setItem(key, JSON.stringify(this.fbLeaderOverride))
      } catch (err) {
        console.warn('[theme-prop] fb leader save failed', err)
      }
    },
    isFbLeaderRow(row) {
      const code = normCode6(row && row.code)
      const cur = normCode6(this.firstBoardLeader && (this.firstBoardLeader.code || this.firstBoardLeader.leaderId))
      return !!(code && cur && code === cur)
    },
    toggleDragonCard(card) {
      const key = card && card.key
      if (!key) return
      const keys = (this.selectedDragonKeys || []).slice()
      const idx = keys.indexOf(key)
      if (idx >= 0) keys.splice(idx, 1)
      else keys.push(key)
      this.selectedDragonKeys = keys
    },
    startLeaderPick() {
      if (!(this.heightManualRows || []).length) {
        this.$message.warning('请先在「非龙头最高高度 · 手动名单」里添加股票')
        return
      }
      this.leaderPickMode = true
      this.buZhangPickMode = false
      this.heightPickMode = false
      this.relayPickMode = false
      this.fbPreselectPickMode = false
      this.fbChiPickMode = false
      this.mainAttrPickMode = false
      this.$nextTick(() => {
        const el = this.$el && this.$el.querySelector && this.$el.querySelector('.manual-table')
        if (el && el.scrollIntoView) el.scrollIntoView({ block: 'nearest' })
      })
    },
    assignFbLeader(row) {
      const code = normCode6(row && row.code)
      const name = String((row && row.name) || '').trim()
      if (!code || code === '000000' || !name) return
      const card = normCode6(this.leader && (this.leader.code || this.leader.leaderId))
      const cur = normCode6(this.fbLeaderOverride && this.fbLeaderOverride.code)
      if (code === card || code === cur) {
        this.fbLeaderOverride = null
      } else {
        this.fbLeaderOverride = { code, name }
        const slots = (this.buZhangSlots || [null, null, null]).slice(0, 3)
        let changed = false
        for (let i = 0; i < slots.length; i++) {
          if (normCode6(slots[i] && slots[i].code) === code) {
            slots[i] = null
            changed = true
          }
        }
        if (changed) {
          this.buZhangSlots = slots
          this.persistBuZhangSlots()
        }
      }
      this.leaderPickMode = false
      this.persistFbLeaderOverride()
    },
    clearLeaderOverride() {
      this.fbLeaderOverride = null
      this.leaderPickMode = false
      this.persistFbLeaderOverride()
    },
    persistBuZhangSlots() {
      const key = this.buZhangLsKey()
      if (!key) return
      try {
        const list = (this.buZhangSlots || []).slice(0, 3).map((s) => (
          s && s.code ? { code: normCode6(s.code), name: s.name || '' } : null
        ))
        while (list.length < 3) list.push(null)
        localStorage.setItem(key, JSON.stringify(list))
      } catch (err) {
        console.warn('[theme-prop] buzhang save failed', err)
      }
    },
    buZhangIndex(row) {
      const code = normCode6(row && row.code)
      if (!code) return -1
      return (this.buZhangSlots || []).findIndex((s) => normCode6(s && s.code) === code)
    },
    startBuZhangPick() {
      if (!(this.heightManualRows || []).length) {
        this.$message.warning('请先在「非龙头最高高度 · 手动名单」里添加股票')
        return
      }
      this.buZhangPickMode = true
      this.leaderPickMode = false
      this.heightPickMode = false
      this.relayPickMode = false
      this.fbPreselectPickMode = false
      this.fbChiPickMode = false
      this.mainAttrPickMode = false
      this.$nextTick(() => {
        const el = this.$el && this.$el.querySelector && this.$el.querySelector('.manual-table')
        if (el && el.scrollIntoView) el.scrollIntoView({ block: 'nearest' })
      })
    },
    tryRoleStock(row, boardLabel) {
      if (!this.rolePick || !row) return false
      const code = normCode6(row.code)
      const name = String(row.name || '').trim()
      if (!code || code === '000000') return false
      this.$emit('role-stock', {
        code,
        name,
        boardLabel: String(boardLabel || row.board || row.lianBanHeight || row.boardLabel || row.finalBoardLabel || '')
      })
      return true
    },
    onRelayRowClick(row) {
      this.tryRoleStock(row, row && (row.boardLabel || ''))
    },
    onThemeGainRowClick(row) {
      if (this.heightBoardTab !== 'd10') return
      this.tryRoleStock(row, '')
    },
    onHeightManualRowClick(row) {
      if (!row) return
      if (this.tryRoleStock(row, row.lianBanHeight || row.finalBoardLabel)) return
      if (this.leaderPickMode) {
        this.assignFbLeader(row)
        return
      }
      if (!this.buZhangPickMode) return
      this.assignBuZhang(row)
    },
    assignBuZhang(row) {
      const code = normCode6(row && row.code)
      const name = String((row && row.name) || '').trim()
      if (!code || code === '000000' || !name) return
      const slots = (this.buZhangSlots || [null, null, null]).slice(0, 3)
      while (slots.length < 3) slots.push(null)
      const idx = slots.findIndex((s) => normCode6(s && s.code) === code)
      if (idx >= 0) {
        slots[idx] = null
      } else {
        const leaderCode = normCode6(this.firstBoardLeader && (this.firstBoardLeader.code || this.firstBoardLeader.leaderId))
        if (code === leaderCode) {
          this.$message.warning('这只已经是龙头，请另选补涨')
          return
        }
        const empty = slots.findIndex((s) => !(s && s.code))
        if (empty < 0) {
          this.$message.warning('补涨已选满 3 只，先取消一只再选')
          return
        }
        slots[empty] = { code, name }
        if (!slots.some((s) => !(s && s.code))) this.buZhangPickMode = false
      }
      this.buZhangSlots = slots
      this.persistBuZhangSlots()
    },
    clearBuZhang(index) {
      const slots = (this.buZhangSlots || [null, null, null]).slice(0, 3)
      while (slots.length < 3) slots.push(null)
      if (index < 0 || index >= slots.length) return
      slots[index] = null
      this.buZhangSlots = slots
      this.persistBuZhangSlots()
    },
    onEmbedConcepts(list) {
      this.fbConcepts = Array.isArray(list) ? list : []
      this.persistFbConcepts()
    },
    onEmbedSubAttrs(attrs) {
      this.fbSubAttrs = {
        attrSub1: (attrs && attrs.attrSub1) || '',
        attrSub2: (attrs && attrs.attrSub2) || '',
        attrSub3: (attrs && attrs.attrSub3) || ''
      }
      this.persistFbSubAttrs()
    },
    onEmbedLeaderMeta(meta) {
      this.fbLeaderMeta = {
        priceText: (meta && meta.priceText) || '—',
        ffText: (meta && meta.ffText) || '—',
        title: (meta && meta.title) || ''
      }
    },
    fbSubAttrHit(name) {
      const n = String(name || '').trim()
      if (!n) return false
      return FB_SUB_SLOTS.some((slot) => this.fbSubAttrs[slot.key] === n)
    },
    openFbAttrMenu(evt, name) {
      const concept = String(name || '').trim()
      if (!concept) return
      if (this.fbAttrMenu.visible && this.fbAttrMenu.concept === concept) {
        this.closeFbAttrMenu()
        return
      }
      const rect = evt && evt.currentTarget && evt.currentTarget.getBoundingClientRect
        ? evt.currentTarget.getBoundingClientRect()
        : null
      const x = rect ? rect.left : (evt && evt.clientX) || 12
      const y = rect ? rect.bottom + 4 : (evt && evt.clientY) || 12
      this.fbAttrMenu = {
        visible: true,
        x: Math.max(8, Math.min(x, window.innerWidth - 220)),
        y: Math.max(8, Math.min(y, window.innerHeight - 220)),
        concept
      }
    },
    closeFbAttrMenu() {
      if (!this.fbAttrMenu.visible) return
      this.fbAttrMenu = { visible: false, x: 0, y: 0, concept: '' }
    },
    assignFbSubAttr(key) {
      const concept = this.fbAttrMenu.concept
      const fb = this.$refs.firstBoard
      if (fb && typeof fb.setEmbedSubAttr === 'function') fb.setEmbedSubAttr(key, concept)
      this.closeFbAttrMenu()
    },
    onDocClickCloseFbMenu(e) {
      if (!this.fbAttrMenu.visible) return
      const t = e && e.target
      if (t && t.closest && (t.closest('.fb-attr-menu') || t.closest('.fb-concept-tag'))) return
      this.closeFbAttrMenu()
    },
    heightLsKey() {
      const code = this.leader ? normCode6(this.leader.code || this.leader.leaderId) : ''
      if (!code || code === '000000') return ''
      // 名单按龙头代码保存，不跟日期、不跟当前筛选。不能按「当前可见列表」删 key，
      // 否则换日 / 切到曾入池 / 搜索会把不在这份列表里的龙头名单清掉。
      return `${HEIGHT_LS_PREFIX}|${code}`
    },
    normalizeHeightRow(r) {
      const ff =
        r && r.freeFloatMarketCapYi != null && Number.isFinite(Number(r.freeFloatMarketCapYi))
          ? Number(r.freeFloatMarketCapYi)
          : null
      const prem =
        r && r.premium12Pct != null && Number.isFinite(Number(r.premium12Pct))
          ? Number(r.premium12Pct)
          : null
      return {
        id: (r && r.id) || emptyHeightRow().id,
        code: (r && r.code) || '',
        name: (r && r.name) || '',
        lianBanHeight: (r && r.lianBanHeight) || '',
        finalBoardLabel: (r && r.finalBoardLabel) || '',
        totalReturnText: (r && r.totalReturnText) != null ? String(r.totalReturnText) : '',
        freeFloatMarketCapYi: ff,
        premium12Pct: prem,
        conceptNames: Array.isArray(r && r.conceptNames) ? r.conceptNames.slice() : []
      }
    },
    formatFreeFloatYi(v) {
      if (v == null || !Number.isFinite(Number(v))) return '—'
      const n = Math.round(Number(v) * 100) / 100
      return n + '亿'
    },
    mergeHeightRowsFromStorageKeys(prefixes) {
      const byCode = new Map()
      try {
        for (let i = 0; i < localStorage.length; i += 1) {
          const k = localStorage.key(i)
          if (!k) continue
          if (!prefixes.some((p) => k.startsWith(p))) continue
          const parsed = JSON.parse(localStorage.getItem(k) || '[]')
          if (!Array.isArray(parsed)) continue
          for (const r of parsed) {
            const row = this.normalizeHeightRow(r)
            const c = normCode6(row.code)
            if (!c || c === '000000') continue
            if (!byCode.has(c)) byCode.set(c, row)
          }
        }
      } catch (err) {
        console.warn('[theme-prop] height migrate merge failed', err)
      }
      return [...byCode.values()]
    },
    loadHeightRows() {
      const key = this.heightLsKey()
      this.heightStorageKey = key
      if (!key) {
        this.heightManualRows = []
        return
      }
      const code = this.leader ? normCode6(this.leader.code || this.leader.leaderId) : ''
      const td = String(this.tradeDate || '').trim()
      try {
        const raw = localStorage.getItem(key)
        let list = []
        if (raw) {
          const parsed = JSON.parse(raw)
          list = Array.isArray(parsed) ? parsed.map((r) => this.normalizeHeightRow(r)) : []
        }
        if (!list.length && code) {
          // 旧 key：v2=…|日期|龙头，v1=…|日期|龙头|题材
          const exactV2 = td
            ? this.mergeHeightRowsFromStorageKeys([`${HEIGHT_LS_PREFIX_V2}|${td}|${code}`])
            : []
          if (exactV2.length) {
            list = exactV2
          } else {
            const allOld = []
            try {
              for (let i = 0; i < localStorage.length; i += 1) {
                const k = localStorage.key(i)
                if (!k) continue
                const isV2 = k.startsWith(`${HEIGHT_LS_PREFIX_V2}|`) && k.endsWith(`|${code}`)
                const isV1 = k.startsWith(`${HEIGHT_LS_PREFIX_V1}|`) && k.includes(`|${code}|`)
                if (!isV2 && !isV1) continue
                const parsed = JSON.parse(localStorage.getItem(k) || '[]')
                if (!Array.isArray(parsed)) continue
                for (const r of parsed) allOld.push(this.normalizeHeightRow(r))
              }
            } catch (e) {
              /* ignore */
            }
            const byCode = new Map()
            for (const row of allOld) {
              const c = normCode6(row.code)
              if (c && c !== '000000' && !byCode.has(c)) byCode.set(c, row)
            }
            list = [...byCode.values()]
          }
          if (list.length) {
            this.heightManualRows = sortHeightRowsByBoard(list)
            this.persistHeightRows()
            this.$nextTick(() => this.syncHeightRowsFromPools())
            return
          }
        }
        const sorted = sortHeightRowsByBoard(list)
        const before = list.map((r) => (r && r.code) || '').join('|')
        const after = sorted.map((r) => (r && r.code) || '').join('|')
        this.heightManualRows = sorted
        if (before !== after) this.persistHeightRows()
        this.$nextTick(() => this.syncHeightRowsFromPools())
      } catch (err) {
        console.warn('[theme-prop] height manual load failed', err)
        this.heightManualRows = []
      }
    },
    persistHeightRows() {
      const key = this.heightStorageKey || this.heightLsKey()
      if (!key) return
      try {
        const payload = (this.heightManualRows || []).map((r) => ({
          id: r.id,
          code: r.code || '',
          name: r.name || '',
          lianBanHeight: r.lianBanHeight || '',
          finalBoardLabel: r.finalBoardLabel || '',
          totalReturnText: r.totalReturnText || '',
          freeFloatMarketCapYi:
            r.freeFloatMarketCapYi != null && Number.isFinite(Number(r.freeFloatMarketCapYi))
              ? Number(r.freeFloatMarketCapYi)
              : null,
          premium12Pct:
            r.premium12Pct != null && Number.isFinite(Number(r.premium12Pct))
              ? Number(r.premium12Pct)
              : null,
          conceptNames: Array.isArray(r.conceptNames) ? r.conceptNames : []
        }))
        localStorage.setItem(key, JSON.stringify(payload))
      } catch (err) {
        console.warn('[theme-prop] height manual save failed', err)
      }
    },
    toggleHeightPickMode() {
      if (!this.heightLsKey()) return
      this.heightPickMode = !this.heightPickMode
      if (this.heightPickMode) {
        this.relayPickMode = false
        this.fbPreselectPickMode = false
      this.fbChiPickMode = false
        this.mainAttrPickMode = false
        this.buZhangPickMode = false
        this.leaderPickMode = false
      }
    },
    isHeightPicked(code) {
      const c = normCode6(code)
      return !!(c && this.heightPickedCodeSet[c])
    },
    onPoolRowClick(row) {
      if (this.tryRoleStock(row, row && (row.board || ''))) return
      if (!this.heightPickMode || !row) return
      this.toggleHeightFromPool(row)
    },
    findPoolRowByCode(code) {
      const c = normCode6(code)
      if (!c) return null
      for (const key of ['gt4b', 'b3', 'b2']) {
        const rows = (this.oncePools && this.oncePools[key] && this.oncePools[key].rows) || []
        const hit = rows.find((r) => normCode6(r.code) === c)
        if (hit) return hit
      }
      return null
    },
    findMonitorLeader(code) {
      const c = normCode6(code)
      if (!c) return null
      return (this.leaders || []).find((row) => normCode6(row && (row.code || row.leaderId)) === c) || null
    },
    findLimitUpRow(code) {
      const c = normCode6(code)
      if (!c) return null
      return (this.limitUpRows || []).find((row) => normCode6(row && (row.code || row.ts_code)) === c) || null
    },
    conceptNamesForCode(code) {
      const c = normCode6(code)
      const raw = c && this.conceptsByCode ? this.conceptsByCode[c] : null
      if (!Array.isArray(raw)) return []
      return raw.map((name) => String(name || '').trim()).filter(Boolean)
    },
    leaderBoardFacts(leader) {
      if (!leader) return { board: '', finalBoardLabel: '', totalReturnText: '' }
      const label = String(leader.boardTierLabel || leader.nDayMBoard || leader.boardStageLabel || '').trim()
      const matched = label.match(/(\d+)天(\d+)板/)
      const stage = leader.boardStage != null ? Number(leader.boardStage) : null
      const height = stage != null && Number.isFinite(stage) && stage > 0
        ? stage
        : matched
          ? Number(matched[2])
          : null
      const d10 = leader.returns && leader.returns.d10
      return {
        board: height ? `${height}B` : '',
        finalBoardLabel: label && label !== '—' ? label : '',
        totalReturnText: d10 != null && Number.isFinite(Number(d10)) ? formatReturnTextFromPct(d10) : ''
      }
    },
    /** 用最新补涨池字段回填手动名单（最终板 N天M板 / 概念 / 涨幅 / 自由流通 / 1-2溢价） */
    syncHeightRowsFromPools() {
      if (!(this.heightManualRows || []).length) return
      let changed = false
      const next = (this.heightManualRows || []).map((row) => {
        const code = normCode6(row.code)
        const pool = this.findPoolRowByCode(row.code)
        const leader = this.findMonitorLeader(code)
        const lu = this.findLimitUpRow(code)
        const leaderConcepts = this.conceptNamesForCode(code)
        const ffFromMap =
          code && this.onceFreeFloatByCode && this.onceFreeFloatByCode[code] != null
            ? Number(this.onceFreeFloatByCode[code])
            : null
        const premFromMap =
          code && this.oncePremium12ByCode && this.oncePremium12ByCode[code] != null
            ? Number(this.oncePremium12ByCode[code])
            : null
        const leaderBoard = this.leaderBoardFacts(leader)
        const board = pool
          ? pool.board ||
            (pool.finalBoard != null && Number.isFinite(Number(pool.finalBoard))
              ? `${Number(pool.finalBoard)}B`
              : row.lianBanHeight)
          : leaderBoard.board || row.lianBanHeight
        const nDayM = pool
          ? pool.nDayMBoard || row.finalBoardLabel || ''
          : leaderBoard.finalBoardLabel || row.finalBoardLabel
        const concepts = pool && Array.isArray(pool.conceptNames) && pool.conceptNames.length
          ? pool.conceptNames.slice()
          : leaderConcepts.length
            ? leaderConcepts
            : row.conceptNames || []
        const ret =
          pool && pool.totalReturnPct != null
            ? formatReturnTextFromPct(pool.totalReturnPct)
            : leaderBoard.totalReturnText || row.totalReturnText
        const luFf = lu && lu.freeFloatMarketCap != null ? Number(lu.freeFloatMarketCap) : null
        const ff =
          pool && pool.freeFloatMarketCapYi != null && Number.isFinite(Number(pool.freeFloatMarketCapYi))
            ? Number(pool.freeFloatMarketCapYi)
            : ffFromMap != null && Number.isFinite(ffFromMap)
              ? ffFromMap
              : luFf != null && Number.isFinite(luFf)
                ? luFf
                : row.freeFloatMarketCapYi
        const prem =
          pool && pool.premium12Pct != null && Number.isFinite(Number(pool.premium12Pct))
            ? Number(pool.premium12Pct)
            : premFromMap != null && Number.isFinite(premFromMap)
              ? premFromMap
              : row.premium12Pct
        if (
          board === row.lianBanHeight &&
          nDayM === row.finalBoardLabel &&
          ret === row.totalReturnText &&
          ff === row.freeFloatMarketCapYi &&
          prem === row.premium12Pct &&
          JSON.stringify(concepts) === JSON.stringify(row.conceptNames || [])
        ) {
          return row
        }
        changed = true
        return {
          ...row,
          name: (pool && pool.name) || row.name,
          lianBanHeight: board,
          finalBoardLabel: nDayM,
          totalReturnText: ret,
          freeFloatMarketCapYi: ff,
          premium12Pct: prem,
          conceptNames: concepts
        }
      })
      if (!changed) return
      this.heightManualRows = sortHeightRowsByBoard(next)
      this.persistHeightRows()
    },
    toggleHeightFromPool(row) {
      if (!this.heightLsKey() || !row) return
      const code = normCode6(row.code)
      if (!code || code === '000000') return
      const list = [...(this.heightManualRows || [])]
      const idx = list.findIndex((r) => normCode6(r.code) === code)
      if (idx >= 0) {
        list.splice(idx, 1)
        this.heightManualRows = list
        this.persistHeightRows()
        return
      }
      const board =
        row.board ||
        (row.finalBoard != null && Number.isFinite(Number(row.finalBoard))
          ? `${Number(row.finalBoard)}B`
          : '')
      list.push({
        id: emptyHeightRow().id,
        code,
        name: row.name || code,
        lianBanHeight: board,
        finalBoardLabel: row.nDayMBoard || '',
        totalReturnText: formatReturnTextFromPct(row.totalReturnPct),
        freeFloatMarketCapYi:
          row.freeFloatMarketCapYi != null && Number.isFinite(Number(row.freeFloatMarketCapYi))
            ? Number(row.freeFloatMarketCapYi)
            : this.onceFreeFloatByCode[code] != null
              ? Number(this.onceFreeFloatByCode[code])
              : null,
        premium12Pct:
          row.premium12Pct != null && Number.isFinite(Number(row.premium12Pct))
            ? Number(row.premium12Pct)
            : this.oncePremium12ByCode[code] != null
              ? Number(this.oncePremium12ByCode[code])
              : null,
        conceptNames: Array.isArray(row.conceptNames) ? row.conceptNames.slice() : []
      })
      this.heightManualRows = sortHeightRowsByBoard(list)
      this.persistHeightRows()
      this.syncHeightRowsFromPools()
    },
    removeHeightRow(idx) {
      const list = [...(this.heightManualRows || [])]
      if (idx < 0 || idx >= list.length) return
      list.splice(idx, 1)
      this.heightManualRows = list
      this.persistHeightRows()
    },
    relayLsKey() {
      const code = this.leader ? normCode6(this.leader.code || this.leader.leaderId) : ''
      const td = String(this.tradeDate || '').trim()
      if (!code || code === '000000' || !td) return ''
      return `${RELAY_LS_PREFIX}|${td}|${code}`
    },
    normalizeRelayRow(r) {
      const concepts = Array.isArray(r && r.matchedConcepts)
        ? r.matchedConcepts
          .map((c) => ({
            name: String((c && c.name) || '').trim(),
            type: (c && c.type) || '',
            score: finiteOrNull(c && c.score)
          }))
          .filter((c) => c.name)
        : []
      return {
        id: (r && r.id) || emptyRelayRow().id,
        code: (r && r.code) || '',
        name: (r && r.name) || '',
        midChip: finiteOrNull(r && r.midChip),
        rangeAllD0: finiteOrNull(r && r.rangeAllD0),
        marketValue: finiteOrNull(r && r.marketValue),
        boardLabel: (r && r.boardLabel) || '',
        conceptScoreSum: finiteOrNull(r && r.conceptScoreSum),
        matchedConcepts: concepts,
        preScreenScore: finiteOrNull(r && r.preScreenScore),
        nodeScore: finiteOrNull(r && r.nodeScore),
        firstBoardDate: String((r && r.firstBoardDate) || '').trim(),
        nodeHit: (() => {
          const ns = finiteOrNull(r && r.nodeScore)
          return ns != null && ns >= 7
        })()
      }
    },
    loadRelayRows() {
      const key = this.relayLsKey()
      this.relayStorageKey = key
      if (!key) {
        this.relayTopRows = []
        return
      }
      try {
        const raw = localStorage.getItem(key)
        const parsed = raw ? JSON.parse(raw) : []
        this.relayTopRows = this.sortRelayRows(Array.isArray(parsed) ? parsed.map((r) => this.normalizeRelayRow(r)) : [])
        this.enrichRelayMeta()
      } catch (err) {
        console.warn('[theme-prop] relay top load failed', err)
        this.relayTopRows = []
      }
    },
    persistRelayRows() {
      const key = this.relayStorageKey || this.relayLsKey()
      if (!key) return
      try {
        const payload = (this.relayTopRows || []).map((r) => this.normalizeRelayRow(r))
        localStorage.setItem(key, JSON.stringify(payload))
      } catch (err) {
        console.warn('[theme-prop] relay top save failed', err)
      }
    },
    toggleRelayPickMode() {
      if (!this.relayLsKey()) return
      this.relayPickMode = !this.relayPickMode
      if (!this.relayPickMode) return
      this.fbPreselectPickMode = false
      this.fbChiPickMode = false
      this.upperPool = 'relay'
      this.heightPickMode = false
      this.mainAttrPickMode = false
      this.buZhangPickMode = false
      this.leaderPickMode = false
      this.setBottomTab('catchup')
      this.$nextTick(() => {
        const el = this.$el && this.$el.querySelector && this.$el.querySelector('.catchup-embed')
        if (el && el.scrollIntoView) el.scrollIntoView({ block: 'nearest' })
      })
    },
    autoFillRelayFromAnalysis(payloads) {
      if (this.relayAwaitMainAttr) return
      if (!this.relayLsKey()) return
      const rows = Array.isArray(payloads) ? payloads : []
      if (!rows.length) return
      const list = [...(this.relayTopRows || [])]
      const seen = new Set(list.map((row) => normCode6(row && row.code)).filter((code) => code && code !== '000000'))
      let added = 0
      rows.forEach((payload) => {
        const code = normCode6(payload && payload.code)
        if (!code || code === '000000' || seen.has(code)) return
        seen.add(code)
        added += 1
        list.push(this.normalizeRelayRow({
          id: emptyRelayRow().id,
          code,
          name: payload.name || code,
          midChip: payload.midChip,
          rangeAllD0: payload.rangeAllD0,
          marketValue: payload.marketValue,
          boardLabel: payload.boardLabel,
          conceptScoreSum: payload.conceptScoreSum,
          matchedConcepts: payload.matchedConcepts,
          preScreenScore: payload.preScreenScore,
          nodeScore: payload.nodeScore,
          firstBoardDate: payload.firstBoardDate
        }))
      })
      if (!added) return
      this.relayTopRows = this.sortRelayRows(list)
      this.persistRelayRows()
    },
    toggleRelayFromAnalysis(payload) {
      if (!this.relayPickMode || !payload) return
      const code = normCode6(payload.code)
      if (!code || code === '000000') return
      const list = [...(this.relayTopRows || [])]
      const idx = list.findIndex((r) => normCode6(r.code) === code)
      if (idx >= 0) {
        list.splice(idx, 1)
        this.relayTopRows = list
        this.persistRelayRows()
        return
      }
      list.push(this.normalizeRelayRow({
        id: emptyRelayRow().id,
        code,
        name: payload.name || code,
        midChip: payload.midChip,
        rangeAllD0: payload.rangeAllD0,
        marketValue: payload.marketValue,
        boardLabel: payload.boardLabel,
        conceptScoreSum: payload.conceptScoreSum,
        matchedConcepts: payload.matchedConcepts,
        preScreenScore: payload.preScreenScore,
        nodeScore: payload.nodeScore,
        firstBoardDate: payload.firstBoardDate
      }))
      this.relayTopRows = this.sortRelayRows(list)
      this.persistRelayRows()
    },
    removeRelayRow(idx) {
      const list = [...(this.relayTopRows || [])]
      if (idx < 0 || idx >= list.length) return
      list.splice(idx, 1)
      this.relayTopRows = list
      this.persistRelayRows()
    },
    fbPreselectLsKey() {
      const code = this.leader ? normCode6(this.leader.code || this.leader.leaderId) : ''
      const td = String(this.tradeDate || '').trim()
      if (!code || code === '000000' || !td) return ''
      return `${FB_PRESELECT_LS_PREFIX}|${td}|${code}`
    },
    normalizeFbPreselectRow(r) {
      const concepts = Array.isArray(r && r.concepts)
        ? r.concepts
          .map((c) => ({
            name: String((c && c.name) || '').trim(),
            kind: (c && c.kind) || 'matched'
          }))
          .filter((c) => c.name)
        : []
      return {
        id: (r && r.id) || emptyRelayRow().id,
        code: (r && r.code) || '',
        name: (r && r.name) || '',
        currentPrice: finiteOrNull(r && r.currentPrice),
        midChip: finiteOrNull(r && r.midChip),
        marketValue: finiteOrNull(r && r.marketValue),
        matchScore: finiteOrNull(r && r.matchScore),
        concepts
      }
    },
    loadFbPreselectRows() {
      const key = this.fbPreselectLsKey()
      this.fbPreselectStorageKey = key
      if (!key) {
        this.fbPreselectRows = []
        return
      }
      try {
        const raw = localStorage.getItem(key)
        const parsed = raw ? JSON.parse(raw) : []
        const list = Array.isArray(parsed) ? parsed.map((row) => this.normalizeFbPreselectRow(row)) : []
        this.fbPreselectRows = this.sortFbPreselectRows(list)
      } catch (err) {
        console.warn('[theme-prop] fb preselect load failed', err)
        this.fbPreselectRows = []
      }
    },
    persistFbPreselectRows() {
      const key = this.fbPreselectStorageKey || this.fbPreselectLsKey()
      if (!key) return
      try {
        const payload = (this.fbPreselectRows || []).map((row) => this.normalizeFbPreselectRow(row))
        localStorage.setItem(key, JSON.stringify(payload))
      } catch (err) {
        console.warn('[theme-prop] fb preselect save failed', err)
      }
    },
    sortFbPreselectRows(list) {
      return (list || []).slice().sort((a, b) => {
        const as = finiteOrNull(a && a.matchScore)
        const bs = finiteOrNull(b && b.matchScore)
        if (as == null && bs == null) return String(a && a.name).localeCompare(String(b && b.name), 'zh')
        if (as == null) return 1
        if (bs == null) return -1
        if (as !== bs) return bs - as
        return String(a && a.name).localeCompare(String(b && b.name), 'zh')
      })
    },
    toggleFbPreselectPickMode() {
      if (!this.fbPreselectLsKey()) return
      this.fbPreselectPickMode = !this.fbPreselectPickMode
      if (!this.fbPreselectPickMode) return
      this.relayPickMode = false
      this.upperPool = 'fbpre'
      this.heightPickMode = false
      this.mainAttrPickMode = false
      this.buZhangPickMode = false
      this.leaderPickMode = false
      this.setBottomTab('firstboard')
      this.$nextTick(() => {
        const el = this.$el && this.$el.querySelector && this.$el.querySelector('.firstboard-embed')
        if (el && el.scrollIntoView) el.scrollIntoView({ block: 'nearest' })
      })
    },
    appendFbPreselectRows(list, payloads, chiOnly) {
      const next = [...(list || [])]
      const seen = new Set(next.map((row) => normCode6(row && row.code)).filter((code) => code && code !== '000000'))
      for (let i = 0; i < (payloads || []).length; i += 1) {
        const payload = payloads[i]
        const code = normCode6(payload && payload.code)
        if (!code || code === '000000' || seen.has(code)) continue
        if (chiOnly && !isChiNextCode(code)) continue
        seen.add(code)
        next.push(this.normalizeFbPreselectRow({
          id: emptyRelayRow().id,
          code,
          name: payload.name || code,
          currentPrice: payload.currentPrice,
          midChip: payload.midChip,
          marketValue: payload.marketValue,
          matchScore: payload.matchScore,
          concepts: payload.concepts
        }))
      }
      return this.sortFbPreselectRows(next)
    },
    selectAllFbPreselect(payloads) {
      if (!this.fbPreselectLsKey()) return
      const rows = Array.isArray(payloads) ? payloads : []
      if (!rows.length) {
        this.$message.warning('当前没有可全选的股票')
        return
      }
      if (!this.fbPreselectPickMode) {
        this.fbPreselectPickMode = true
        this.fbChiPickMode = false
        this.relayPickMode = false
        this.upperPool = 'fbpre'
        this.heightPickMode = false
        this.mainAttrPickMode = false
        this.buZhangPickMode = false
        this.leaderPickMode = false
      }
      this.fbPreselectRows = this.appendFbPreselectRows(this.fbPreselectRows, rows, false)
      this.persistFbPreselectRows()
      this.$message.success(`已全选当前 ${rows.length} 只`)
    },
    selectAllFbChiPreselect(payloads) {
      if (!this.fbChiPreselectLsKey()) return
      const rows = (Array.isArray(payloads) ? payloads : []).filter((row) => isChiNextCode(row && row.code))
      if (!rows.length) {
        this.$message.warning('当前没有可全选的创业板股票')
        return
      }
      if (!this.fbChiPickMode) {
        this.fbChiPickMode = true
        this.fbPreselectPickMode = false
        this.relayPickMode = false
        this.upperPool = 'fbchi'
        this.heightPickMode = false
        this.mainAttrPickMode = false
        this.buZhangPickMode = false
        this.leaderPickMode = false
      }
      this.fbChiPreselectRows = this.appendFbPreselectRows(this.fbChiPreselectRows, rows, true)
      this.persistFbChiPreselectRows()
      this.$message.success(`已全选当前 ${rows.length} 只`)
    },
    toggleFbPreselectFromAnalysis(payload) {
      if (!this.fbPreselectPickMode || !payload) return
      const code = normCode6(payload.code)
      if (!code || code === '000000') return
      const list = [...(this.fbPreselectRows || [])]
      const idx = list.findIndex((row) => normCode6(row.code) === code)
      if (idx >= 0) {
        list.splice(idx, 1)
        this.fbPreselectRows = list
        this.persistFbPreselectRows()
        return
      }
      list.push(this.normalizeFbPreselectRow({
        id: emptyRelayRow().id,
        code,
        name: payload.name || code,
        currentPrice: payload.currentPrice,
        midChip: payload.midChip,
        marketValue: payload.marketValue,
        matchScore: payload.matchScore,
        concepts: payload.concepts
      }))
      this.fbPreselectRows = this.sortFbPreselectRows(list)
      this.persistFbPreselectRows()
    },
    fbChiPreselectLsKey() {
      const code = this.leader ? normCode6(this.leader.code || this.leader.leaderId) : ''
      const td = String(this.tradeDate || '').trim()
      if (!code || code === '000000' || !td) return ''
      return `${FB_CHI_PRESELECT_LS_PREFIX}|${td}|${code}`
    },
    loadFbChiPreselectRows() {
      const key = this.fbChiPreselectLsKey()
      this.fbChiPreselectStorageKey = key
      if (!key) {
        this.fbChiPreselectRows = []
        return
      }
      try {
        const raw = localStorage.getItem(key)
        const parsed = raw ? JSON.parse(raw) : []
        const list = Array.isArray(parsed) ? parsed.map((row) => this.normalizeFbPreselectRow(row)) : []
        this.fbChiPreselectRows = this.sortFbPreselectRows(list.filter((row) => isChiNextCode(row.code)))
      } catch (err) {
        console.warn('[theme-prop] fb chi preselect load failed', err)
        this.fbChiPreselectRows = []
      }
    },
    persistFbChiPreselectRows() {
      const key = this.fbChiPreselectStorageKey || this.fbChiPreselectLsKey()
      if (!key) return
      try {
        const payload = (this.fbChiPreselectRows || []).map((row) => this.normalizeFbPreselectRow(row))
        localStorage.setItem(key, JSON.stringify(payload))
      } catch (err) {
        console.warn('[theme-prop] fb chi preselect save failed', err)
      }
    },
    toggleFbChiPickMode() {
      if (!this.fbChiPreselectLsKey()) return
      this.fbChiPickMode = !this.fbChiPickMode
      if (!this.fbChiPickMode) return
      this.fbPreselectPickMode = false
      this.relayPickMode = false
      this.upperPool = 'fbchi'
      this.heightPickMode = false
      this.mainAttrPickMode = false
      this.buZhangPickMode = false
      this.leaderPickMode = false
      this.setBottomTab('firstboardChi')
    },
    toggleFbChiFromAnalysis(payload) {
      if (!this.fbChiPickMode || !payload) return
      const code = normCode6(payload.code)
      if (!code || code === '000000') return
      if (!isChiNextCode(code)) {
        this.$message.warning('创业预选只接收创业板')
        return
      }
      const list = [...(this.fbChiPreselectRows || [])]
      const idx = list.findIndex((row) => normCode6(row.code) === code)
      if (idx >= 0) {
        list.splice(idx, 1)
        this.fbChiPreselectRows = list
        this.persistFbChiPreselectRows()
        return
      }
      list.push(this.normalizeFbPreselectRow({
        id: emptyRelayRow().id,
        code,
        name: payload.name || code,
        currentPrice: payload.currentPrice,
        midChip: payload.midChip,
        marketValue: payload.marketValue,
        matchScore: payload.matchScore,
        concepts: payload.concepts
      }))
      this.fbChiPreselectRows = this.sortFbPreselectRows(list)
      this.persistFbChiPreselectRows()
    },
    removeActiveFbPreselectRow(idx) {
      if (this.upperPool === 'fbchi') this.removeFbChiPreselectRow(idx)
      else this.removeFbPreselectRow(idx)
    },
    removeFbChiPreselectRow(idx) {
      const list = [...(this.fbChiPreselectRows || [])]
      if (idx < 0 || idx >= list.length) return
      list.splice(idx, 1)
      this.fbChiPreselectRows = list
      this.persistFbChiPreselectRows()
    },
    removeFbPreselectRow(idx) {
      const list = [...(this.fbPreselectRows || [])]
      if (idx < 0 || idx >= list.length) return
      list.splice(idx, 1)
      this.fbPreselectRows = list
      this.persistFbPreselectRows()
    },
    formatFbPrice(v) {
      const n = finiteOrNull(v)
      if (n == null) return '—'
      return n.toFixed(2)
    },
    formatFbChip(v) {
      const n = finiteOrNull(v)
      if (n == null) return '—'
      return `${(n * 100).toFixed(2)}%`
    },
    sortRelayRows(list) {
      return (list || []).slice().sort((a, b) => {
        const am = finiteOrNull(a && a.midChip)
        const bm = finiteOrNull(b && b.midChip)
        if (am == null && bm == null) return String(a && a.name).localeCompare(String(b && b.name), 'zh')
        if (am == null) return 1
        if (bm == null) return -1
        if (am !== bm) return am - bm
        return String(a && a.name).localeCompare(String(b && b.name), 'zh')
      })
    },
    relayNodeColor(score) {
      const s = Number(score)
      if (!Number.isFinite(s) || s < 7) return null
      const t = Math.max(0, Math.min(1, (s - 7) / 15))
      const sat = Math.round(82 + (88 - 82) * t)
      const light = Math.round(42 + (24 - 42) * t)
      return `hsl(180, ${sat}%, ${light}%)`
    },
    relayNodeStarStyle(score) {
      const c = this.relayNodeColor(score)
      return c ? { color: c } : null
    },
    relayNodeStarTitle(row) {
      const s = finiteOrNull(row && row.nodeScore)
      const fb = row && row.firstBoardDate ? `首板日 ${row.firstBoardDate}` : '首板日'
      return `${fb} · 节点分 ${s == null ? '—' : s} · ≥7`
    },
    formatRelayPreScreen(v) {
      const n = finiteOrNull(v)
      if (n == null) return '—'
      return n.toFixed(1)
    },
    async enrichRelayMeta() {
      const rows = this.relayTopRows || []
      const td = String(this.tradeDate || '').trim()
      if (!rows.length || !td) return
      const seq = (this.relayMetaSeq || 0) + 1
      this.relayMetaSeq = seq
      let lu = []
      try {
        const res = await stockApi.getLimitUpStocks(td)
        lu = (res && res.data) || []
      } catch (err) {
        lu = []
      }
      if (seq !== this.relayMetaSeq) return
      const byCode = {}
      lu.forEach((stock) => {
        const code = normCode6(stock && (stock.code || stock.ts_code))
        if (code) byCode[code] = stock
      })
      const dates = []
      const seen = new Set()
      rows.forEach((row) => {
        const hit = byCode[normCode6(row.code)] || {}
        const fb = String(row.firstBoardDate || hit.firstBoardDate || '').trim()
        if (fb && !seen.has(fb)) {
          seen.add(fb)
          dates.push(fb)
        }
      })
      const scores = {}
      await Promise.all(dates.map(async (d) => {
        try {
          const doc = await stockApi.fetchDailyData(d)
          const s = doc && doc.greedyNodeScore
          scores[d] = s != null && s !== '' && Number.isFinite(Number(s)) ? Number(s) : null
        } catch (err) {
          scores[d] = null
        }
      }))
      if (seq !== this.relayMetaSeq) return
      this.relayTopRows = this.sortRelayRows(rows.map((row) => {
        const hit = byCode[normCode6(row.code)] || {}
        const fb = String(row.firstBoardDate || hit.firstBoardDate || '').trim()
        const fromDaily = fb ? scores[fb] : null
        const fromPool = finiteOrNull(hit.nodeScore)
        const liveNode = fromDaily != null ? fromDaily : fromPool
        const ns = liveNode != null ? liveNode : finiteOrNull(row.nodeScore)
        const livePre = finiteOrNull(hit.preScreenScore)
        const pre = livePre != null ? livePre : finiteOrNull(row.preScreenScore)
        return this.normalizeRelayRow({
          ...row,
          firstBoardDate: fb,
          nodeScore: ns,
          preScreenScore: pre
        })
      }))
      this.persistRelayRows()
    },
    formatRelayMidChip(v) {
      const n = finiteOrNull(v)
      if (n == null) return '—'
      return `${(n * 100).toFixed(2)}%`
    },
    formatRelayRangeAll(v) {
      const n = finiteOrNull(v)
      if (n == null) return '—'
      return `${(n * 100).toFixed(1)}%`
    },
    formatRelayMarketYi(v) {
      const n = finiteOrNull(v)
      if (n == null) return '—'
      const rounded = Math.round(n * 100) / 100
      return `${rounded}亿`
    },
    formatRelayConceptSum(v) {
      const n = finiteOrNull(v)
      if (n == null) return '—'
      return String(Math.round(n * 100) / 100)
    },
    relayConceptTagClass(tag) {
      const type = tag && tag.type
      if (type === 'highlight') return 'hot'
      if (type === 'repeat') return 'repeat'
      return ''
    },
    conceptsForHeightRow(row) {
      const tags = this.conceptTagsForHeightRow(row)
      return tags.length ? tags.map((t) => t.name).join('、') : '—'
    },
    heightMoodTags(row) {
      const code = normCode6(row && row.code)
      const tags = code && this.heightMoodByCode ? this.heightMoodByCode[code] : null
      return Array.isArray(tags) ? tags : []
    },
    async loadThemeWindowReturns() {
      const td = String(this.tradeDate || '').trim()
      const concepts = []
      const seen = new Set()
      for (const raw of this.mainAttrs || []) {
        const name = stripThemeLabel(raw)
        if (!name || seen.has(name)) continue
        seen.add(name)
        concepts.push(name)
      }
      const seq = ++this.themeGainSeq
      if (!td || !concepts.length) {
        this.themeGainWindows = { 10: [], 15: [], 20: [] }
        this.themeGainLoading = false
        return
      }
      this.themeGainLoading = true
      try {
        const res = await stockApi.postThemeWindowReturns({ tradeDate: td, concepts })
        if (seq !== this.themeGainSeq) return
        const windows = (res && res.windows) || {}
        this.themeGainWindows = {
          10: Array.isArray(windows[10] || windows['10']) ? (windows[10] || windows['10']) : [],
          15: Array.isArray(windows[15] || windows['15']) ? (windows[15] || windows['15']) : [],
          20: Array.isArray(windows[20] || windows['20']) ? (windows[20] || windows['20']) : []
        }
      } catch (err) {
        if (seq !== this.themeGainSeq) return
        console.warn('[theme-prop] theme window returns failed', err)
        this.themeGainWindows = { 10: [], 15: [], 20: [] }
      } finally {
        if (seq === this.themeGainSeq) this.themeGainLoading = false
      }
    },
    async loadHeightMood() {
      const td = String(this.tradeDate || '').trim()
      const codes = []
      const seen = new Set()
      for (const row of this.heightManualRows || []) {
        const code = normCode6(row && row.code)
        if (!code || code === '000000' || seen.has(code)) continue
        seen.add(code)
        codes.push(code)
      }
      const seq = ++this.heightMoodSeq
      if (!td || !codes.length) {
        this.heightMoodByCode = {}
        return
      }
      try {
        const res = await stockApi.postHeightMood({ tradeDate: td, codes })
        if (seq !== this.heightMoodSeq) return
        this.heightMoodByCode = (res && res.byCode) || {}
      } catch (err) {
        if (seq !== this.heightMoodSeq) return
        console.warn('[theme-prop] height mood failed', err)
        this.heightMoodByCode = {}
      }
    },
    conceptTagsForHeightRow(row) {
      const top = this.top10ThemeBareNames || []
      if (!top.length) return []
      const names = Array.isArray(row && row.conceptNames) ? row.conceptNames : []
      const rates = this.heightConceptAppearRate || {}
      const out = []
      const seen = new Set()
      for (const raw of names) {
        const c = String(raw || '').trim()
        if (!c || seen.has(c) || !top.includes(c)) continue
        seen.add(c)
        const rate = rates[c]
        out.push({
          name: c,
          hot: rate != null && Number(rate) > 75
        })
      }
      return out
    },
    formatHeightReturn(text) {
      const n = parseReturnPct(text)
      if (n == null) return text && String(text).trim() ? String(text).trim() : '—'
      return this.formatPct(n)
    },
    resetOnce4b() {
      this.oncePools = {
        gt4b: emptyPoolBlock(),
        b3: emptyPoolBlock(),
        b2: emptyPoolBlock()
      }
      this.once4bMeta = null
      this.onceFreeFloatByCode = {}
      this.oncePremium12ByCode = {}
      this.leaderSinceReturnPct = null
      this.once4bLoading = false
    },
    themeSourceOf(label) {
      const row = ((this.pack && this.pack.themeRows) || []).find((r) => r && r.name === label)
      const text = String(label || '')
      if (row && row.source === 'KPL') return 'KPL'
      if (row && row.source === '固定') return '固定'
      if (row && row.source === 'WC') return 'WC'
      if (/[（(]\s*KPL\s*[）)]\s*$/i.test(text)) return 'KPL'
      if (/[（(]\s*固定\s*[）)]\s*$/i.test(text)) return '固定'
      if (/[（(]\s*WC\s*[）)]\s*$/i.test(text)) return 'WC'
      return 'MERGE'
    },
    async loadOnce4b() {
      const leader = this.leader
      const theme = this.selectedTheme
      const td = this.tradeDate
      if (!leader || !theme || !td) {
        this.resetOnce4b()
        return
      }
      const seq = ++this.once4bSeq
      this.once4bLoading = true
      try {
        const res = await stockApi.postLeaderOnce4b({
          tradeDate: td,
          leaderCode: normCode6(leader.code || leader.leaderId),
          concept: theme,
          conceptSource: this.themeSourceOf(theme),
          exitDate: leader.exitDate || '',
          anchorDate: (this.pack && this.pack.anchorDate) || ''
        })
        if (seq !== this.once4bSeq) return
        this.oncePools = this.takeOncePools(res)
        const ffMap = {}
        const rawFf = (res && res.freeFloatByCode) || {}
        for (const [k, v] of Object.entries(rawFf)) {
          const c = normCode6(k)
          if (c && v != null && Number.isFinite(Number(v))) ffMap[c] = Number(v)
        }
        // 池内兜底
        for (const key of ['gt4b', 'b3', 'b2']) {
          for (const r of (this.oncePools[key] && this.oncePools[key].rows) || []) {
            const c = normCode6(r.code)
            if (
              c &&
              ffMap[c] == null &&
              r.freeFloatMarketCapYi != null &&
              Number.isFinite(Number(r.freeFloatMarketCapYi))
            ) {
              ffMap[c] = Number(r.freeFloatMarketCapYi)
            }
          }
        }
        this.onceFreeFloatByCode = ffMap
        const premMap = {}
        for (const key of ['gt4b', 'b3', 'b2']) {
          for (const r of (this.oncePools[key] && this.oncePools[key].rows) || []) {
            const c = normCode6(r.code)
            if (c && r.premium12Pct != null && Number.isFinite(Number(r.premium12Pct))) {
              premMap[c] = Number(r.premium12Pct)
            }
          }
        }
        this.oncePremium12ByCode = premMap
        this.syncHeightRowsFromPools()
        this.once4bMeta = {
          anchorDate: res && res.anchorDate,
          baseDate: res && res.baseDate,
          windowEnd: res && res.windowEnd
        }
        this.leaderSinceReturnPct =
          res && res.leaderSinceReturnPct != null && Number.isFinite(Number(res.leaderSinceReturnPct))
            ? Number(res.leaderSinceReturnPct)
            : null
      } catch (err) {
        if (seq !== this.once4bSeq) return
        console.warn('[theme-prop] once-4b failed', err)
        this.resetOnce4b()
      } finally {
        if (seq === this.once4bSeq) this.once4bLoading = false
      }
    },
    takeOncePools(res) {
      const pools = (res && res.pools) || {}
      const pick = (key, fallbackRows) => {
        const block = pools[key]
        const rawRows = block && Array.isArray(block.rows) ? block.rows : fallbackRows
        const rows = mapPoolApiRows(rawRows)
        return {
          count: block && block.count != null ? block.count : rows.length,
          rows,
          totalReturnPct: block && block.totalReturnPct != null ? block.totalReturnPct : avgPct(rows, 'totalReturnPct'),
          todayRedRatePct: block && block.todayRedRatePct != null ? block.todayRedRatePct : redRatePct(rows)
        }
      }
      return {
        gt4b: pick('gt4b', (res && res.rows) || []),
        b3: pick('b3', []),
        b2: pick('b2', [])
      }
    },
    async loadConfirmPools() {
      if (!this.sectorKey) return
      const leader = this.leader
      const theme = String(this.sectorConcept || '').trim()
      const td = this.tradeDate
      const empty = {
        gt4b: emptyPoolBlock(),
        b3: emptyPoolBlock(),
        b2: emptyPoolBlock()
      }
      if (!leader || !theme || !td) {
        this.confirmPools = empty
        this.confirmLoading = false
        return
      }
      const seq = ++this.confirmSeq
      this.confirmLoading = true
      try {
        const source = this.sectorSource === 'KPL' || this.sectorSource === 'WC'
          ? this.sectorSource
          : this.themeSourceOf(theme)
        const res = await stockApi.postLeaderOnce4b({
          tradeDate: td,
          leaderCode: normCode6(leader.code || leader.leaderId),
          concept: theme,
          conceptSource: source,
          exitDate: leader.exitDate || '',
          anchorDate: (this.pack && this.pack.anchorDate) || ''
        })
        if (seq !== this.confirmSeq) return
        this.confirmPools = this.takeOncePools(res)
      } catch (err) {
        if (seq !== this.confirmSeq) return
        console.warn('[theme-prop] confirm pool failed', err)
        this.confirmPools = empty
      } finally {
        if (seq === this.confirmSeq) this.confirmLoading = false
      }
    },
    setSort(key) {
      if (!key) return
      if (this.sortKey === key) {
        this.sortDesc = !this.sortDesc
        return
      }
      this.sortKey = key
      this.sortDesc = true
    },
    sortMark(key) {
      if (this.sortKey !== key) return ''
      return this.sortDesc ? ' ↓' : ' ↑'
    },
    dash(v) {
      if (v == null || v === '') return '—'
      return v
    },
    formatYi(v) {
      if (v == null || !Number.isFinite(Number(v))) return '—'
      const n = Math.round(Number(v) * 100) / 100
      const sign = n > 0 ? '+' : ''
      return sign + n
    },
    formatPct(v) {
      if (v == null || !Number.isFinite(Number(v))) return '—'
      const n = Number(v)
      const sign = n > 0 ? '+' : ''
      return sign + (Math.round(n * 100) / 100) + '%'
    },
    formatDelta(v) {
      if (v == null || !Number.isFinite(Number(v))) return ''
      const n = Number(v)
      const sign = n > 0 ? '+' : ''
      return sign + (Math.round(n * 100) / 100)
    },
    formatPlainPct(v) {
      if (v == null || !Number.isFinite(Number(v))) return '—'
      return Math.round(Number(v)) + '%'
    },
    formatMult(v) {
      if (v == null || !Number.isFinite(Number(v))) return '—'
      return (Math.round(Number(v) * 100) / 100) + 'x'
    },
    chgClass(v) {
      if (v == null || !Number.isFinite(Number(v))) return ''
      if (Number(v) > 0) return 'up'
      if (Number(v) < 0) return 'down'
      return ''
    },
    pickConcept(c) {
      this.selectedTheme = c
      this.conceptDialogVisible = false
    }
  }
}
</script>

<style scoped lang="scss">
.tp-center {
  background: #fff;
  border: 1px solid #d7e4f6;
  border-radius: 12px;
  padding: 12px;
  min-height: 0;
  overflow: auto;
}
.empty {
  padding: 40px;
  text-align: center;
  color: #8aa0c2;
}
.stock-head {
  margin-bottom: 16px;
  padding-bottom: 14px;
  border-bottom: 1px solid #eef3fa;
}
.name-line {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 10px 18px;
  margin-bottom: 6px;
}
.name {
  font-size: 26px;
  font-weight: 800;
  color: #1a2b4a;
  letter-spacing: 0.5px;
}
.code {
  color: #8aa0c2;
  font-size: 16px;
  margin-left: -8px;
}
.chg {
  font-weight: 800;
  font-size: 26px;
  letter-spacing: 0.3px;
}
.delta {
  font-size: 16px;
  font-weight: 700;
  margin-left: -8px;
}
.px, .since {
  display: inline-flex;
  align-items: baseline;
  gap: 8px;
  font-size: 15px;
  color: #5b6b82;
  b {
    font-size: 18px;
    font-weight: 800;
    color: #1f2d3d;
  }
}
.leader-quote {
  display: inline-flex;
  align-items: baseline;
  gap: 4px 8px;
  font-size: 12px;
  color: #5b6b82;
  white-space: nowrap;
  b {
    font-size: 14px;
    font-weight: 800;
    color: #1f2d3d;
  }
}
.concept-btn {
  height: 28px;
  padding: 0 12px;
  border: 1px solid #7eb0f0;
  border-radius: 6px;
  background: #e8f1ff;
  color: #1a73e8;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  align-self: center;
  &:hover:not(:disabled) { background: #d6e8ff; }
  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }
}
.up { color: #d00000; }
.down { color: #67c23a; }
.chips-hint {
  font-size: 11px;
  color: #8aa0c2;
}
.selected-theme-hint {
  margin-top: 2px;
  font-size: 12px;
  color: #8aa0c2;
  b {
    color: #1a73e8;
    font-weight: 700;
  }
}
.chips {
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
}
.dialog-chips {
  max-height: 420px;
  overflow: auto;
  padding: 2px 0;
}
.chip {
  border: 1px solid #d6e4f5;
  background: #f3f7fc;
  color: #5b7ea8;
  border-radius: 8px;
  padding: 2px 7px;
  font-size: 11px;
  line-height: 1.3;
  cursor: pointer;
  &.on {
    background: #e8f1ff;
    border-color: #7eb0f0;
    color: #1a73e8;
    font-weight: 700;
  }
}
.block { margin-top: 12px; }
.block.pick-active {
  border-radius: 12px;
  box-shadow: 0 0 0 1px rgba(26, 115, 232, 0.35);
}
.catchup-embed.pick-active {
  border-radius: 8px;
  box-shadow: 0 0 0 1px rgba(26, 115, 232, 0.28);
}
.block-h {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
  .h { font-size: 14px; font-weight: 800; }
}
.block-tabs {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-right: 4px;
}
.block-tab {
  border: 1px solid #d7e4f6;
  background: #f7faff;
  color: #35598c;
  border-radius: 14px;
  padding: 4px 12px;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  line-height: 1.4;
  &.on {
    background: #1a73e8;
    color: #fff;
    border-color: #1a73e8;
  }
}
.catchup-embed {
  min-height: 560px;
  border: 1px solid #eef3fa;
  border-radius: 8px;
  overflow: hidden;
  background: #fff;
}
.table-wrap { overflow-x: auto; }
.theme-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 11px;
  th, td {
    padding: 7px 6px;
    border-bottom: 1px solid #eef3fa;
    text-align: right;
    white-space: nowrap;
  }
  th:nth-child(1), th:nth-child(2),
  td:nth-child(1), td:nth-child(2) { text-align: left; }
  th { color: #6b82a6; font-weight: 650; background: #f7fafd; }
  th.th-stack {
    white-space: normal;
    text-align: center;
    line-height: 1.25;
    span { display: block; }
  }
  th.th-sort {
    cursor: pointer;
    user-select: none;
    &:hover { color: #1a73e8; }
    &.on { color: #1a73e8; font-weight: 800; }
  }
  tbody tr {
    cursor: pointer;
    &:hover { background: #f5f9ff; }
    &.on { background: #eaf2ff; }
  }
  .name-cell { font-weight: 700; color: #1f2d3d; }
  .empty-row { text-align: center; color: #8aa0c2; padding: 18px 8px; }
  .expand-row td {
    text-align: center;
    padding: 10px 8px;
    border-bottom: none;
  }
}
.expand-btn {
  border: 1px solid #d0d7e2;
  background: #f7fafd;
  color: #1a73e8;
  border-radius: 6px;
  padding: 5px 14px;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  &:hover { border-color: #1a73e8; background: #eef5ff; }
}
.red { color: #f56c6c; font-weight: 700; }
.gain { color: #d00000; font-weight: 800; }
.up { color: #d00000; font-weight: 700; }
.down { color: #67c23a; font-weight: 700; }
.heat {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}
.bars {
  display: inline-flex;
  gap: 2px;
  i {
    width: 3px;
    height: 10px;
    background: #dce6f5;
    border-radius: 1px;
    display: inline-block;
    &:nth-child(2) { height: 12px; }
    &:nth-child(3) { height: 14px; }
    &:nth-child(4) { height: 16px; }
    &:nth-child(5) { height: 18px; }
    &.on { background: #f56c6c; }
  }
}
.diffusion-block {
  margin-top: 4px;
  margin-bottom: 14px;
  background: #fff;
  border: 1px solid #d7e6fb;
  border-radius: 12px;
  padding: 10px 12px 12px;
  box-shadow: 0 1px 0 rgba(26, 115, 232, 0.04);
}
.diffusion-head {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin-bottom: 10px;
}
.diffusion-head .sync-ths-btn {
  margin-left: auto;
  flex-shrink: 0;
  align-self: center;
  padding: 5px 10px;
  font-size: 12px;
}
.diffusion-ico {
  width: 22px;
  height: 22px;
  border-radius: 6px;
  background: #1a73e8;
  color: #fff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  flex-shrink: 0;
  margin-top: 1px;
}
.diffusion-titles {
  display: flex;
  flex-direction: row;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 8px;
  min-width: 0;
}
.diffusion-title {
  font-size: 14px;
  font-weight: 800;
  color: #1a2b4a;
}
.diffusion-sub {
  font-size: 11px;
  color: #8aa0c2;
  line-height: 1.35;
}
.diffusion-metrics {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  padding: 6px 10px;
  margin-bottom: 10px;
  background: #f7fafd;
  border: 1px solid #e8eef7;
  border-radius: 8px;
}
.attr-slot {
  display: inline-flex;
  align-items: center;
  flex: 0 0 auto;
  gap: 6px;
  width: auto;
  padding: 2px 4px 2px 8px;
  border: 1px solid #d5e2f4;
  border-radius: 4px;
  background: #fff;
  &.filled {
    border-color: #7eb0f0;
    background: #f7fbff;
  }
  &.next {
    border-color: #1a73e8;
  }
}
.attr-slot-label {
  font-size: 12px;
  font-weight: 700;
  color: #5b6b82;
  line-height: 1;
  white-space: nowrap;
}
.attr-slot .attr-add {
  height: 22px;
  padding: 0 8px;
  border-radius: 3px;
  font-size: 12px;
}
.attr-slot-name {
  max-width: 8em;
  font-size: 12px;
  font-weight: 700;
  color: #1f2d3d;
  line-height: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.attr-clear {
  border: none;
  background: transparent;
  color: #8aa0c2;
  cursor: pointer;
  font-size: 14px;
  line-height: 1;
  padding: 0 2px;
  &:hover { color: #e85d5d; }
}
.tab-badge {
  margin-left: 6px;
  font-size: 10px;
  font-weight: 700;
  color: #1a73e8;
}
.table-wrap.attr-picking .theme-table tbody tr.attr-picked {
  background: #fff6e8;
}
.attr-mark {
  margin-left: 4px;
  font-size: 10px;
  font-weight: 700;
  color: #e67e22;
}
.metric-label {
  font-size: 11px;
  color: #8aa0c2;
  margin-bottom: 2px;
}
.metric-value {
  font-size: 22px;
  font-weight: 800;
  color: #1a73e8;
  letter-spacing: 0.3px;
  line-height: 1.2;
}
.pool-switch {
  justify-content: flex-start;
  gap: 0;
}
.height-board-h {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 2px;
}
.height-board-h .manual-add {
  margin-left: auto;
}
.height-board-h .pool-tab {
  white-space: nowrap;
}
.pool-tab {
  border: 0;
  background: transparent;
  color: #5b6b82;
  font-size: 13px;
  font-weight: 700;
  padding: 4px 10px 6px;
  cursor: pointer;
  border-bottom: 2px solid transparent;
  &.on {
    color: #1a73e8;
    border-bottom-color: #1a73e8;
  }
}
.height-boards {
  display: flex;
  align-items: flex-start;
  gap: 12px;
}
.manual-pool {
  flex: 0 1 auto;
  width: max-content;
  max-width: 62%;
  min-width: 0;
}
.relay-pool {
  flex: 1 1 280px;
  min-width: 240px;
}
.manual-pool .manual-pool-h,
.relay-pool .manual-pool-h {
  width: 100%;
}
.relay-table-wrap {
  display: block;
  width: 100%;
}
.relay-pool .manual-table {
  width: 100%;
}
.tp-center .node-star {
  margin-right: 2px;
  color: #13c2c2;
  font-weight: 700;
}
.manual-pool-title {
  font-size: 12px;
  font-weight: 700;
  color: #5b6b82;
}
.manual-add {
  height: 26px;
  padding: 0 10px;
  border: 1px solid #7eb0f0;
  border-radius: 6px;
  background: #e8f1ff;
  color: #1a73e8;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  &:hover { background: #d6e8ff; }
  &.on {
    background: #1a73e8;
    border-color: #1a73e8;
    color: #fff;
  }
}
.pick-hint {
  margin: -2px 0 8px;
  font-size: 11px;
  color: #1a73e8;
  font-weight: 600;
}
.pick-badge {
  margin-left: 4px;
  padding: 2px 8px;
  border-radius: 999px;
  background: #1a73e8;
  color: #fff;
  font-size: 11px;
  font-weight: 700;
}
.catchup-block.pick-active {
  border-color: #1a73e8;
  box-shadow: 0 0 0 1px rgba(26, 115, 232, 0.18);
}
.manual-table-wrap {
  display: inline-block;
  max-width: 100%;
  vertical-align: top;
  border: 1px solid #e8eef7;
  border-radius: 8px;
  overflow-x: auto;
}
.manual-table {
  width: max-content;
  max-width: 100%;
  table-layout: auto;
  border-collapse: collapse;
  font-size: 11px;
  &.role-picking tbody tr.pickable {
    cursor: pointer;
    &:hover { background: #eaf2ff; }
  }
  th, td {
    padding: 4px 6px;
    border-bottom: 1px solid #eef3fa;
    text-align: left;
    white-space: nowrap;
    vertical-align: middle;
  }
  th {
    color: #6b82a6;
    font-weight: 650;
    background: #f7fafd;
  }
  .th-name, .nm {
    min-width: 4.5em;
    font-weight: 700;
    color: #1f2d3d;
  }
  .th-h, .h {
    min-width: 3.2em;
    text-align: center;
  }
  .th-ndm, .ndm {
    min-width: 4.5em;
    font-variant-numeric: tabular-nums;
    color: #1f2d3d;
  }
  .th-pct, .pct {
    min-width: 4.5em;
    font-variant-numeric: tabular-nums;
    font-weight: 700;
  }
  .th-ff, .ff {
    min-width: 3.8em;
    font-variant-numeric: tabular-nums;
    color: #5b6b82;
    white-space: nowrap;
  }
  .th-prem, .prem {
    min-width: 4.2em;
    font-variant-numeric: tabular-nums;
    font-weight: 700;
    white-space: nowrap;
  }
  .th-concept, .concept {
    white-space: normal;
    line-height: 1.35;
    max-width: 14em;
  }
  .concept-empty { color: #8aa0c2; }
  .mood-tag {
    display: inline-block;
    margin: 0 4px 2px 0;
    padding: 1px 6px;
    border-radius: 4px;
    background: #1f9d55;
    color: #fff;
    font-size: 12px;
    font-weight: 700;
    line-height: 1.4;
    white-space: nowrap;
  }
  .th-chip, .chip {
    min-width: 4.6em;
    text-align: center;
    font-variant-numeric: tabular-nums;
    color: #1f2d3d;
  }
  .th-score, .score {
    min-width: 3.6em;
    text-align: center;
    font-variant-numeric: tabular-nums;
    font-weight: 700;
    color: #1f2d3d;
  }
  .concept-tag {
    display: inline-block;
    margin: 1px 3px 1px 0;
    padding: 0 5px;
    border-radius: 3px;
    background: #eef3fa;
    color: #5b6b82;
    font-size: 10px;
    font-weight: 650;
    line-height: 1.5;
    white-space: nowrap;
    &.hot {
      background: #f48fb1;
      color: #fff;
    }
    &.repeat {
      background: #fde2e2;
      color: #c45656;
    }
  }
  .col-act {
    width: 1.4em;
    padding-left: 2px;
    padding-right: 4px;
    text-align: center;
  }
  tbody tr:last-child td { border-bottom: none; }
}
.manual-del {
  width: 24px;
  height: 24px;
  border: none;
  background: transparent;
  color: #90a4c0;
  font-size: 16px;
  line-height: 1;
  cursor: pointer;
  border-radius: 4px;
  &:hover { color: #d00000; background: #fff1f0; }
}
.manual-empty {
  text-align: center !important;
  color: #8aa0c2;
  padding: 14px 8px !important;
}
.catchup-block {
  margin-top: 14px;
  background: #f4f8ff;
  border: 1px solid #d7e6fb;
  border-radius: 12px;
  padding: 10px;
}
.catchup-head {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin-bottom: 10px;
}
.catchup-ico {
  width: 22px;
  height: 22px;
  border-radius: 6px;
  background: #1a73e8;
  color: #fff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
}
.catchup-title {
  font-size: 14px;
  font-weight: 800;
  color: #1a73e8;
}
.veteran-tabs {
  display: inline-flex;
  gap: 6px;
}
.veteran-tabs button,
.veteran-save,
.veteran-clear {
  border: 1px solid #d5e4f7;
  background: #fff;
  color: #35598c;
  border-radius: 8px;
  padding: 4px 10px;
  cursor: pointer;
  font-size: 12px;
}
.veteran-tabs button.on {
  background: #1a73e8;
  color: #fff;
  border-color: #1a73e8;
}
.veteran-save {
  background: #1a73e8;
  color: #fff;
  border-color: #1a73e8;
}
.veteran-clear {
  margin-left: auto;
  color: #c2410c;
  border-color: #f0c2a8;
  background: #fff7f2;
}
.pool-table .chk {
  width: 28px;
  text-align: center;
}
.concept-analysis-empty {
  padding: 4px 2px 2px;
  font-size: 12px;
  color: #8aa0c2;
}
.fb-concepts {
  display: flex;
  flex-wrap: wrap;
  gap: 12px 14px;
  padding: 8px 2px 2px;
}
.fb-count-badge ::v-deep .el-badge__content.is-fixed {
  top: -6px;
  right: -8px;
  height: 16px;
  line-height: 16px;
  padding: 0 4px;
  font-size: 11px;
  font-weight: 700;
  background: #409eff;
  border: none;
  transform: none;
}
.fb-concept-tag { cursor: pointer; }
.fb-attr-menu {
  position: fixed;
  z-index: 40;
  min-width: 168px;
  padding: 6px;
  background: #fff;
  border: 1px solid #d7e6fb;
  border-radius: 8px;
  box-shadow: 0 8px 24px rgba(26, 43, 74, 0.12);
}
.fb-attr-menu__title {
  padding: 4px 8px 6px;
  font-size: 12px;
  color: #5b6b82;
}
.fb-attr-menu__item {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  width: 100%;
  padding: 6px 8px;
  border: none;
  background: transparent;
  border-radius: 4px;
  cursor: pointer;
  font-size: 13px;
  color: #1f2d3d;
  text-align: left;
  &:hover { background: #f3f8ff; }
}
.fb-attr-menu__cur {
  color: #1a73e8;
  font-size: 12px;
}
.buzhang-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
}
.dragon-card-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
}
.dragon-card-row .search-area-label {
  color: #606266;
  font-size: 13px;
}
.dragon-card {
  display: inline-flex;
  flex-direction: column;
  align-items: flex-start;
  border: 1px solid #d5e4f7;
  background: #f7faff;
  border-radius: 8px;
  padding: 2px 8px 3px;
  cursor: pointer;
  line-height: 1.15;
}
.dragon-card em {
  font-style: normal;
  font-size: 10px;
  color: #7a8eab;
}
.dragon-card b {
  font-size: 12px;
  font-weight: 700;
  color: #243044;
}
.dragon-card.on.tone-leader {
  border-color: #e0b15a;
  background: #fff6e4;
  box-shadow: 0 0 0 1px #e0b15a;
}
.dragon-card.on.tone-trend {
  border-color: #1a73e8;
  background: #e8f1ff;
  box-shadow: 0 0 0 1px #1a73e8;
}
.dragon-card.on.tone-board {
  border-color: #e07aa2;
  background: #fff1f6;
  box-shadow: 0 0 0 1px #e07aa2;
}
.firstboard-embed {
  min-height: 520px;
}
.manual-table.bz-picking tbody tr { cursor: pointer; }
.manual-table tbody tr.bz-picked td { background: #fff6e8; }
.pools {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;
}
.pool {
  background: #fff;
  border: 1px solid #e4edf8;
  border-radius: 10px;
  padding: 8px 8px 4px;
  min-width: 0;
}
.pool-top {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin-bottom: 6px;
}
.pool-tag {
  display: inline-flex;
  align-items: center;
  height: 22px;
  padding: 0 8px;
  border-radius: 4px;
  color: #fff;
  font-size: 12px;
  font-weight: 800;
}
.tone-blue .pool-tag { background: #3b82f6; }
.tone-orange .pool-tag { background: #f59e0b; }
.tone-green .pool-tag { background: #22c55e; }
.pool-stats {
  display: flex;
  flex-wrap: wrap;
  gap: 8px 12px;
  font-size: 11px;
  color: #8aa0c2;
  b { color: #1f2d3d; font-weight: 800; }
  b.up { color: #d00000; }
}
.pool-table-wrap { overflow: hidden; }
.pool-table {
  width: 100%;
  table-layout: fixed;
  border-collapse: collapse;
  font-size: 10px;
  .col-idx { width: 1.2em; }
  .col-name { width: 24%; }
  .col-code { width: 4.6em; }
  .col-board { width: 3.2em; }
  .col-pct { width: auto; }
  th, td {
    padding: 4px 2px;
    text-align: right;
    border-bottom: 1px solid #f0f4fa;
  }
  th:nth-child(2),
  td:nth-child(2) { padding-right: 1px; }
  th:nth-child(3),
  td:nth-child(3) {
    padding-left: 1px;
    font-size: 10px;
  }
  th:nth-child(-n+3),
  td:nth-child(-n+3) { text-align: left; }
  th { color: #90a4c0; font-weight: 650; }
  .nm {
    font-weight: 700;
    color: #1f2d3d;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .code {
    color: #5b6b82;
    font-size: 10px;
    font-variant-numeric: tabular-nums;
    letter-spacing: 0;
    white-space: nowrap;
  }
  tr.grp-head td {
    text-align: left;
    font-weight: 800;
    color: #1a73e8;
    background: #f3f8ff;
    padding-top: 8px;
    padding-bottom: 4px;
    border-bottom: 1px solid #d7e6fb;
  }
  &.picking tbody tr.pickable,
  &.role-picking tbody tr.pickable {
    cursor: pointer;
    &:hover { background: #eaf2ff; }
    &.picked {
      background: #e8f1ff;
      outline: 1px solid rgba(26, 115, 232, 0.25);
      outline-offset: -1px;
    }
  }
}
@media (max-width: 1200px) {
  .pools { grid-template-columns: 1fr; }
}
</style>
